<template>
  <span>
    <b-button
      v-if="show_button"
      size="sm"
      :class="`btn-ai-quick ${button_class}`"
      @click="open"
    >
      <img src="@/assets/icon-ai.svg" alt="" class="kl-ai-icon-sm mr-1" />
      {{ $t("reusable.aiInstructionalBuilderModal.openButton") }}
    </b-button>

    <!-- `ignore-enforce-focus-selector` y no `no-enforce-focus`: es lo que deja
         escribir en la barra de IA lateral sin que el modal robe el foco. -->
    <b-modal
      v-model="visible"
      size="xl"
      dialog-class="kl-ai-dialog"
      body-class="kl-ai-body"
      footer-class="kl-ai-footer"
      ignore-enforce-focus-selector="#sidebar-right"
      :no-close-on-backdrop="busy"
      :no-close-on-esc="busy"
      @hidden="reset"
    >
      <template #modal-header>
        <div class="d-flex w-100 flex-column">
          <div class="d-flex align-items-start justify-content-between">
            <div class="d-flex align-items-start">
              <img src="@/assets/icon-ai.svg" alt="" class="kl-ai-icon-lg mr-2" />
              <div>
                <h5 class="kl-ai-title mb-0">
                  {{ $t("reusable.aiInstructionalBuilderModal.title") }}
                </h5>
                <small class="text-muted">
                  {{
                    step === "setup"
                      ? $t("reusable.aiInstructionalBuilderModal.subtitleSetup")
                      : $t("reusable.aiInstructionalBuilderModal.subtitleReview")
                  }}
                </small>
              </div>
            </div>
            <b-button
              v-if="!busy"
              variant="link"
              size="sm"
              class="kl-ai-close p-0"
              :aria-label="$t('reusable.aiInstructionalBuilderModal.close')"
              @click="close"
            >
              <b-icon icon="x" font-scale="1.4"></b-icon>
            </b-button>
          </div>
          <!-- Indicador de paso: dos tramos, no numeros. Con dos pasos un
               stepper numerado pesa mas de lo que informa. -->
          <div class="kl-ai-steps mt-2" aria-hidden="true">
            <span class="kl-ai-step kl-ai-step-done"></span>
            <span
              class="kl-ai-step"
              :class="{ 'kl-ai-step-done': step === 'review' }"
            ></span>
          </div>
        </div>
      </template>

      <b-alert :show="error != null" variant="danger" class="py-2">
        <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
        {{ error }}
      </b-alert>

      <!-- Paso 1 · Configurar -->
      <template v-if="step === 'setup'">
        <!-- ① La línea de tiempo. Es el ÚNICO input que decide qué se crea.
             Una lista de piezas en orden: qué es cada una y a qué Unidad pertenece.
             Nada de "pedile 3 sumativas y que la IA las ordene": lo que se compone acá es
             literalmente lo que se crea, porque viaja como casilleros y no como pedido. -->
        <div
          v-if="study_units.length > 0 && method_moments.length > 0"
          class="kl-ai-block kl-ai-block-main mb-3"
        >
          <div class="kl-ai-block-head">
            <b-icon icon="list-ol" class="kl-ai-block-icon"></b-icon>
            <span class="kl-ai-label">
              {{ $t("reusable.aiInstructionalBuilderModal.timelineTitle") }}
            </span>
            <!-- Cómo funciona todo esto, PLEGADO. Explicado de corrido ocupaba media
                 pantalla y dejaba la lista de actividades reducida a un par de filas, que
                 es justo lo que hay que mirar para armarla. Se abre cuando hace falta y se
                 vuelve a cerrar; queda abierto mientras el modal siga abierto. -->
            <b-button
              variant="link"
              size="sm"
              class="kl-ai-help-btn ml-auto p-0"
              :aria-expanded="help_open ? 'true' : 'false'"
              @click="help_open = !help_open"
            >
              <b-icon
                :icon="help_open ? 'chevron-up' : 'question-circle'"
                class="mr-1"
              ></b-icon>
              {{ $t("reusable.aiInstructionalBuilderModal.howItWorks") }}
            </b-button>
            <!-- Qué sale de la plataforma al presionar Generar. Lo piden las
                 instituciones: quieren poder ver, sin leer código, con qué datos se arma
                 el pedido, qué parte la decide la IA y qué nunca viaja. En su propio modal
                 porque es una lista larga y no se consulta mientras se arma la lista. -->
            <b-button
              variant="link"
              size="sm"
              class="kl-ai-help-btn ml-2 p-0"
              @click="$bvModal.show(promptModalId)"
            >
              <b-icon icon="box-arrow-up" class="mr-1"></b-icon>
              {{ $t("reusable.aiInstructionalBuilderModal.promptTitle") }}
            </b-button>
          </div>
          <b-collapse v-model="help_open">
            <div class="kl-ai-help mb-2">
              <p class="kl-ai-hint mt-0 mb-2">
                {{
                  $t("reusable.aiInstructionalBuilderModal.timelineHint", {
                    entity: containerEntityName,
                    moments: method_moments.length,
                  })
                }}
              </p>
              <!-- Qué significa "Automático" en la columna del Momento: es la única opción
                   de ese selector que no nombra un momento. -->
              <p class="kl-ai-hint mt-0 mb-0">
                {{
                  $t("reusable.aiInstructionalBuilderModal.momentHint", {
                    entity: containerEntityName,
                  })
                }}
              </p>
            </div>
          </b-collapse>
          <!-- Cuántas Unidades de Estudio hay. Este sí queda a la vista: no es una
               explicación sino un dato de ESTA asignatura, y es con lo que uno decide
               cuántos Módulos tiene sentido armar. -->
          <p class="kl-ai-hint mb-2">
            {{ unitsText }}
          </p>

          <!-- Lo que el diseño YA tiene. De sólo lectura: cambiar o regenerar una pieza
               creada se hace en el diseño, con su formulario. Acá sólo se le AGREGA, que es
               lo que hace falta al reabrir el modal sobre una tanda anterior. -->
          <!-- Lo que el diseño tiene SIN Módulo. Son, casi siempre, evaluaciones que
               vienen del Programa de antes de que existiera el Diseño: cuentan para la
               ponderación —ahí abajo se les puede reajustar el peso— pero no tienen lugar
               en la secuencia, y por eso no se les puede agregar nada alrededor. Agruparlas
               en un Módulo es lo que les da ese lugar; de ahí en más se comportan como
               cualquier otro Módulo del diseño. -->
          <div v-if="loosePieces.length > 0" class="kl-ai-timeline mb-2">
            <div class="kl-ai-module">
              <div class="kl-ai-run-head d-flex align-items-center">
                <b-icon icon="folder-x" class="mr-1"></b-icon>
                <span class="kl-ai-ellipsis flex-grow-1">
                  {{
                    $t("reusable.aiInstructionalBuilderModal.looseTitle", {
                      entity: containerEntityName,
                    })
                  }}
                </span>
                <b-button
                  variant="link"
                  size="sm"
                  class="kl-ai-toggle p-0"
                  :disabled="busy || grouping"
                  @click="groupLoosePieces"
                >
                  <b-spinner v-if="grouping" small></b-spinner>
                  <b-icon v-else icon="folder-plus"></b-icon>
                  {{
                    $t("reusable.aiInstructionalBuilderModal.looseGroupAction", {
                      entity: containerEntityName,
                    })
                  }}
                </b-button>
              </div>
              <div
                v-for="pieza in loosePieces"
                :key="`loose-${pieza.id}`"
                class="kl-ai-row kl-ai-row-in-run kl-ai-row-locked"
              >
                <b-icon icon="lock" class="kl-ai-cycle-num"></b-icon>
                <b-badge
                  :variant="typeVariant(pieza.type)"
                  pill
                  class="kl-ai-col-type"
                >
                  {{ typeLabel(pieza.type) }}
                </b-badge>
                <span class="flex-grow-1 kl-ai-ellipsis kl-ai-small">
                  {{ pieza.name }}
                </span>
              </div>
              <p class="kl-ai-hint mb-0 px-2 pb-1">
                {{
                  $t("reusable.aiInstructionalBuilderModal.looseGroupHint", {
                    entity: containerEntityName,
                  })
                }}
              </p>
            </div>
          </div>

          <div v-if="existingModules.length > 0" class="kl-ai-timeline mb-2">
            <div
              v-for="modulo in existingModules"
              :key="`mod-${modulo.id}`"
              class="kl-ai-module"
            >
              <div class="kl-ai-run-head d-flex align-items-center">
                <b-icon icon="folder" class="mr-1"></b-icon>
                <span
                  v-b-tooltip.v-secondary.noninteractive.hover="modulo.title"
                  class="kl-ai-ellipsis flex-grow-1"
                  >{{ modulo.title }}</span
                >
                <b-button
                  variant="link"
                  size="sm"
                  class="kl-ai-toggle p-0"
                  :disabled="busy || !canAddPiece"
                  @click="addToModule(modulo, 0)"
                >
                  <b-icon icon="plus"></b-icon>
                  {{ $t("reusable.aiInstructionalBuilderModal.type4") }}
                </b-button>
                <!-- La fila ya trae su propio selector de tipo, con los cuatro y en colores,
                     así que el botón no necesita preguntar antes: agrega la evaluación y el
                     tipo se cambia ahí mismo, en la fila. Un menú flotante se recortaba
                     contra el borde del modal —hace scroll— y sacarlo de él agregaba barras
                     de scroll; una fila de opciones aparte se leía como una pieza más. -->
                <b-button
                  variant="link"
                  size="sm"
                  class="kl-ai-toggle p-0 ml-2"
                  :disabled="busy || !canAddPiece"
                  @click="addToModule(modulo, DEFAULT_EVAL_TYPE)"
                >
                  <b-icon icon="plus"></b-icon>
                  {{ $t("reusable.aiInstructionalBuilderModal.cycleEvaluation") }}
                </b-button>
              </div>
              <template v-for="pieza in modulo.pieces">
                <div
                  :key="`mp-${pieza.id}`"
                  class="kl-ai-row kl-ai-row-in-run"
                  :class="{ 'kl-ai-row-locked': redoing !== pieza.id }"
                >
                  <b-icon icon="lock" class="kl-ai-cycle-num"></b-icon>
                  <b-badge
                    :variant="typeVariant(pieza.type)"
                    pill
                    class="kl-ai-col-type"
                  >
                    {{ typeLabel(pieza.type) }}
                  </b-badge>
                  <span class="flex-grow-1 kl-ai-ellipsis kl-ai-small">
                    {{ pieza.name }}
                  </span>
                  <b-spinner
                    v-if="redoing === pieza.id"
                    small
                    variant="primary"
                  ></b-spinner>
                  <b-button
                    v-else
                    variant="link"
                    size="sm"
                    class="kl-ai-icon-btn"
                    :disabled="busy"
                    :title="$t('reusable.aiInstructionalBuilderModal.redo')"
                    @click="openRedo(pieza)"
                  >
                    <b-icon icon="arrow-repeat"></b-icon>
                  </b-button>
                </div>
              </template>
              <!-- Lo que se le suma en esta tanda, debajo de lo que ya tenía. -->
              <template v-for="index in indexesForModule(modulo.id)">
              <div
                :key="`ml-${index}`"
                class="kl-ai-row kl-ai-row-in-run"
                :class="{ 'kl-ai-row-eval': timeline[index].value }"
              >
                <span class="kl-ai-cycle-num">+</span>
                <b-form-select
                  v-model="timeline[index].value"
                  :options="pieceOptions"
                  :class="`kl-ai-type-${timeline[index].value}`"
                  size="sm"
                  class="kl-ai-col-type"
                  :disabled="busy"
                ></b-form-select>
                <!-- Un Módulo del que no se pudo deducir la Unidad —sin Unidades cargadas y
                     sin piezas con Indicadores— la pide acá. Sin ella la fila viajaría como
                     la integradora del curso, y una actividad así el backend la descarta. -->
                <b-form-select
                  v-if="timeline[index].unit == null"
                  v-model="timeline[index].unit"
                  :options="unitOptions(timeline[index])"
                  size="sm"
                  class="kl-ai-col-unit"
                  :disabled="busy"
                ></b-form-select>
                <!-- Acá SÍ se elige el momento, y sólo acá. En un ciclo nuevo el momento es
                     la posición en el recorrido; pero un Módulo que ya recorrió el método
                     entero no tiene posición libre que asignar, y todo lo que se le sumaba
                     caía en el último momento. Vacío sigue siendo automático. -->
                <b-form-select
                  :value="(timeline[index].moments || [])[0] || null"
                  :options="momentOptions(index)"
                  size="sm"
                  class="flex-grow-1"
                  :disabled="busy"
                  @change="setMoment(index, $event)"
                ></b-form-select>
                <b-button
                  variant="link"
                  size="sm"
                  class="kl-ai-icon-btn"
                  :class="{ 'kl-ai-icon-on': timeline[index].theme }"
                  :disabled="busy"
                  :title="
                    $t('reusable.aiInstructionalBuilderModal.themeTitle') +
                      ' (' +
                      $t('reusable.aiInstructionalBuilderModal.optional') +
                      ')'
                  "
                  @click="toggleRowTheme(timeline[index])"
                >
                  <b-icon icon="lightbulb"></b-icon>
                </b-button>
                <b-button
                  variant="link"
                  size="sm"
                  class="kl-ai-icon-btn"
                  :disabled="busy"
                  :title="$t('reusable.aiInstructionalBuilderModal.discard')"
                  @click="removePiece(index)"
                >
                  <b-icon icon="x"></b-icon>
                </b-button>
              </div>
              <div
                v-if="timeline[index].theme_open"
                :key="`ml-theme-${index}`"
                class="kl-ai-row-theme"
              >
                <b-form-input
                  v-model="timeline[index].theme"
                  size="sm"
                  maxlength="200"
                  :placeholder="
                    $t('reusable.aiInstructionalBuilderModal.rowThemePlaceholder')
                  "
                  :disabled="busy"
                ></b-form-input>
              </div>
              </template>
            </div>
          </div>

          <p
            v-if="newRuns.length === 0 && existingModules.length === 0"
            class="kl-ai-empty mb-0"
          >
            {{
              $t("reusable.aiInstructionalBuilderModal.timelineEmpty", {
                entity: containerEntityName,
              })
            }}
          </p>

          <div v-else-if="newRuns.length > 0" class="kl-ai-timeline">
            <div class="kl-ai-row kl-ai-row-head">
              <span class="kl-ai-cycle-num"></span>
              <!-- Cada columna explica qué es: el paso 1 se entiende, o no se usa. -->
              <span
                v-b-tooltip.v-secondary.noninteractive.hover="{ title: $t('reusable.aiInstructionalBuilderModal.colTypeHint'), boundary: 'viewport' }"
                class="kl-ai-col-type kl-ai-col-help"
              >
                {{ $t("reusable.aiInstructionalBuilderModal.colType") }}
              </span>
              <span
                v-b-tooltip.v-secondary.noninteractive.hover="{ title: $t('reusable.aiInstructionalBuilderModal.colUnitHint', {
                    indicator: $getVisibleNames(
                      'teaching.evaluationcriteriamicro',
                      true,
                      'Indicadores de Logro'
                    ),
                  }), boundary: 'viewport' }"
                class="kl-ai-col-unit kl-ai-col-help"
              >
                {{ $t("reusable.aiInstructionalBuilderModal.colUnit") }}
              </span>
              <span
                v-b-tooltip.v-secondary.noninteractive.hover="{ title: $t('reusable.aiInstructionalBuilderModal.colMomentHint', {
                    entity: containerEntityName,
                  }), boundary: 'viewport' }"
                class="flex-grow-1 kl-ai-col-help"
              >
                {{ $t("reusable.aiInstructionalBuilderModal.colMoment") }}
              </span>
              <span class="kl-ai-col-act"></span>
            </div>
            <!-- Un ciclo = un módulo. Se dibuja agrupado porque es la unidad real del
                 diseño: lo que el docente reconoce es "el Módulo 2", no "las filas 6 a 9". -->
            <template v-for="(run, run_index) in newRuns">
              <div
                :key="`run-${run_index}`"
                class="kl-ai-run-head d-flex align-items-center"
              >
                <b-icon icon="folder" class="mr-1"></b-icon>
                <span
                  v-b-tooltip.v-secondary.noninteractive.hover="runLabel(run)"
                  class="kl-ai-ellipsis flex-grow-1"
                >
                  {{ runLabel(run) }}
                </span>
                <span class="text-muted ml-1 text-nowrap">
                  · {{ run.indexes.length }}
                </span>
                <!-- Agregar DENTRO de este Módulo. Los botones de abajo suman siempre al
                     final, así que para meter algo en el Módulo 2 de cinco había que
                     agregarlo al último y subirlo fila por fila. Son los mismos botones
                     que ya tenían los Módulos creados. -->
                <b-button
                  v-b-tooltip.v-secondary.noninteractive.hover="{ title: $t('reusable.aiInstructionalBuilderModal.addActivityTooltip', {
                    entity: containerEntityName,
                  }), boundary: 'viewport' }"
                  variant="link"
                  size="sm"
                  class="kl-ai-toggle p-0 ml-2"
                  :disabled="busy || !canAddPiece"
                  @click="addToRun(run, 0)"
                >
                  <b-icon icon="plus"></b-icon>
                  {{ $t("reusable.aiInstructionalBuilderModal.type4") }}
                </b-button>
                <!-- La fila ya trae su propio selector de tipo, con los cuatro y en colores,
                     así que el botón no necesita preguntar antes: agrega la evaluación y el
                     tipo se cambia ahí mismo, en la fila. Un menú flotante se recortaba
                     contra el borde del modal —hace scroll— y sacarlo de él agregaba barras
                     de scroll; una fila de opciones aparte se leía como una pieza más. -->
                <b-button
                  v-b-tooltip.v-secondary.noninteractive.hover="{ title: $t('reusable.aiInstructionalBuilderModal.addEvaluationTooltip', {
                    entity: containerEntityName,
                  }), boundary: 'viewport' }"
                  variant="link"
                  size="sm"
                  class="kl-ai-toggle p-0 ml-2"
                  :disabled="busy || !canAddPiece"
                  @click="addToRun(run, DEFAULT_EVAL_TYPE)"
                >
                  <b-icon icon="plus"></b-icon>
                  {{ $t("reusable.aiInstructionalBuilderModal.cycleEvaluation") }}
                </b-button>
              </div>
              <template v-for="index in run.indexes">
              <div
                :key="`tl-${index}`"
                class="kl-ai-row kl-ai-row-in-run"
                :class="{ 'kl-ai-row-eval': timeline[index].value }"
              >
                <span class="kl-ai-cycle-num">{{ index + 1 }}</span>
                <b-form-select
                  v-model="timeline[index].value"
                  :options="pieceOptions"
                  :class="`kl-ai-type-${timeline[index].value}`"
                  size="sm"
                  class="kl-ai-col-type"
                  :disabled="busy"
                  @change="onKindChange(timeline[index])"
                ></b-form-select>
                <!-- La Unidad es de dónde salen los Indicadores y contenidos de la pieza,
                     y además decide a qué ciclo pertenece la fila. -->
                <b-form-select
                  v-model="timeline[index].unit"
                  :options="unitOptions(timeline[index])"
                  size="sm"
                  class="kl-ai-col-unit"
                  :disabled="busy"
                  @change="onUnitChange(timeline[index])"
                ></b-form-select>
                <!-- El momento: por defecto es la posición dentro del ciclo, y la primera
                     opción dice cuál sería, así que no hay que elegir para saber qué va a
                     pasar. Se puede fijar igual que en un Módulo que ya existe: el reparto
                     automatico cambia con cada fila que se agrega, y no había forma de decir
                     "esta va en Integración" sin ir moviendola de lugar. -->
                <b-form-select
                  :value="(timeline[index].moments || [])[0] || null"
                  :options="momentOptions(index)"
                  size="sm"
                  class="flex-grow-1"
                  :disabled="busy"
                  @change="setMoment(index, $event)"
                ></b-form-select>
                <span class="kl-ai-col-act text-nowrap">
                  <!-- Separar en un Módulo nuevo. Sin esto, dos ciclos sobre la MISMA
                       Unidad eran un solo Módulo largo, y no había forma de sacar una
                       pieza del ciclo anterior sin cambiarle la Unidad. -->
                  <b-button
                    variant="link"
                    size="sm"
                    class="kl-ai-icon-btn"
                    :class="{ 'kl-ai-icon-on': timeline[index].break_run }"
                    :disabled="busy || index === 0"
                    :title="
                      timeline[index].break_run
                        ? $t('reusable.aiInstructionalBuilderModal.joinRun')
                        : $t('reusable.aiInstructionalBuilderModal.breakRun', {
                            entity: containerEntityName,
                          })
                    "
                    @click="toggleBreak(index)"
                  >
                    <b-icon icon="folder-plus"></b-icon>
                  </b-button>
                  <b-button
                    variant="link"
                    size="sm"
                    class="kl-ai-icon-btn"
                    :disabled="busy || index === 0"
                    :title="$t('reusable.aiInstructionalBuilderModal.moveUp')"
                    @click="movePiece(index, -1)"
                  >
                    <b-icon icon="arrow-up"></b-icon>
                  </b-button>
                  <b-button
                    variant="link"
                    size="sm"
                    class="kl-ai-icon-btn"
                    :disabled="busy || index === timeline.length - 1"
                    :title="$t('reusable.aiInstructionalBuilderModal.moveDown')"
                    @click="movePiece(index, 1)"
                  >
                    <b-icon icon="arrow-down"></b-icon>
                  </b-button>
                  <!-- El tema de ESTA pieza. Antes había UNO para todo el paso 1, y con
                       varias piezas no servía: el prompt le exige a todas reflejar ese
                       tema, así que las diez salían sobre lo mismo. La bombilla queda
                       encendida cuando la fila tiene tema, para que se note con el renglón
                       cerrado. -->
                  <b-button
                    variant="link"
                    size="sm"
                    class="kl-ai-icon-btn"
                    :class="{ 'kl-ai-icon-on': timeline[index].theme }"
                    :disabled="busy"
                    :title="
                      $t('reusable.aiInstructionalBuilderModal.themeTitle') +
                      ' (' +
                      $t('reusable.aiInstructionalBuilderModal.optional') +
                      ')'
                    "
                    @click="toggleRowTheme(timeline[index])"
                  >
                    <b-icon icon="lightbulb"></b-icon>
                  </b-button>
                  <b-button
                    variant="link"
                    size="sm"
                    class="kl-ai-icon-btn"
                    :disabled="busy"
                    :title="$t('reusable.aiInstructionalBuilderModal.discard')"
                    @click="removePiece(index)"
                  >
                    <b-icon icon="x"></b-icon>
                  </b-button>
                </span>
              </div>
              <div
                v-if="timeline[index].theme_open"
                :key="`tl-theme-${index}`"
                class="kl-ai-row-theme"
              >
                <b-form-input
                  v-model="timeline[index].theme"
                  size="sm"
                  maxlength="200"
                  :placeholder="
                    $t('reusable.aiInstructionalBuilderModal.rowThemePlaceholder')
                  "
                  :disabled="busy"
                ></b-form-input>
              </div>
              </template>
            </template>
          </div>

          <!-- Los tres primeros agregan de a UNO, a la izquierda; el último arma un
               Módulo entero de golpe, y por eso va aparte, a la derecha. Antes estaban los
               cuatro en fila y no se distinguía el que hace todo del que hace uno. -->
          <div class="mt-2 d-flex align-items-center flex-wrap">
            <!-- Abre un Módulo vacío sobre la Unidad que aún no se usó. "+ Actividad" y
                 "+ Evaluación" siguen sumando al Módulo en curso; éste empieza otro. -->
            <b-button
              v-b-tooltip.v-secondary.noninteractive.hover="{ title: $t('reusable.aiInstructionalBuilderModal.addModuleTooltip', {
                  entity: containerEntityName,
                }), boundary: 'viewport' }"
              variant="outline-secondary"
              size="sm"
              class="mr-1"
              :disabled="busy || !canAddPiece"
              @click="addModule()"
            >
              <b-icon icon="folder-plus"></b-icon>
              {{ containerEntityName }}
            </b-button>
            <b-button
              v-b-tooltip.v-secondary.noninteractive.hover="{ title: $t('reusable.aiInstructionalBuilderModal.addActivityTooltip', {
                  entity: containerEntityName,
                }), boundary: 'viewport' }"
              variant="outline-primary"
              size="sm"
              class="mr-1"
              :disabled="busy || !canAddPiece"
              @click="addPiece(0)"
            >
              <b-icon icon="plus"></b-icon>
              {{ $t("reusable.aiInstructionalBuilderModal.type4") }}
            </b-button>
            <!-- La fila ya trae su propio selector de tipo, con los cuatro y en colores,
                 así que el botón no necesita preguntar antes: agrega la evaluación y el
                 tipo se cambia ahí mismo, en la fila. -->
            <b-button
              v-b-tooltip.v-secondary.noninteractive.hover="{ title: $t('reusable.aiInstructionalBuilderModal.addEvaluationTooltip', {
                  entity: containerEntityName,
                }), boundary: 'viewport' }"
              variant="outline-primary"
              size="sm"
              class="mr-1"
              :disabled="busy || !canAddPiece"
              @click="addPiece(DEFAULT_EVAL_TYPE)"
            >
              <b-icon icon="plus"></b-icon>
              {{ $t("reusable.aiInstructionalBuilderModal.cycleEvaluation") }}
            </b-button>
            <!-- Sembrar un Módulo completo evita empezar de una fila: es el recorrido
                 entero del método sobre una Unidad, y desde ahí se edita. Se puede pulsar
                 una vez por Unidad, que es lo que no se entendía cuando era un link. -->
            <b-button
              v-b-tooltip.v-secondary.noninteractive.hover="{ title: $t('reusable.aiInstructionalBuilderModal.addCycleTooltip', {
                  entity: containerEntityName,
                }), boundary: 'viewport' }"
              variant="outline-primary"
              size="sm"
              class="ml-auto"
              :disabled="busy || !canAddPiece"
              @click="addCycle()"
            >
              {{
                $t("reusable.aiInstructionalBuilderModal.addCycle", {
                  entity: containerEntityName,
                })
              }}
            </b-button>
          </div>

        </div>

        <!-- Qué pasa con lo que ya se generó. Volver al paso 1 no tira el borrador: se
             conserva lo que sigue en su lugar y sólo se pide lo nuevo. Decirlo acá evita la
             duda de si "Generar" va a rehacer todo. -->
        <b-alert :show="hasDraft" variant="secondary" class="py-2 mb-2">
          <div class="d-flex align-items-start">
            <b-icon icon="bookmark-check" class="mr-2 mt-1"></b-icon>
            <div class="kl-ai-small">
              <!-- Primero POR QUE pasa y después los números, uno por línea. Todo junto en
                   una frase —"se conserva 0, se reescriben 2, se generan 3"— habia que
                   leerlo dos veces: los números se pisaban (las 3 incluían las 2) y no
                   decía por qué algo ya escrito dejaba de servir. -->
              <div v-if="draftExplain" class="mb-1">{{ draftExplain }}</div>
              <div v-for="line in draftLines" :key="line">{{ line }}</div>
            </div>
          </div>
        </b-alert>

        <!-- Resumen de lo que va a pasar. Es lo último que se lee antes de generar, y
             evita la sorpresa de recibir algo distinto de lo que se esperaba. -->
        <b-alert :show="canGenerate" variant="info" class="py-2 mb-0">
          <div class="d-flex align-items-start">
            <b-icon icon="info-circle-fill" class="mr-2 mt-1"></b-icon>
            <div class="kl-ai-small">{{ summaryText }}</div>
          </div>
        </b-alert>
      </template>

      <!-- Paso 2 · Revisar -->
      <template v-else>
        <div v-if="generating" class="d-flex align-items-center mb-3">
          <b-spinner small variant="primary" class="mr-2"></b-spinner>
          <small class="text-muted">
            {{
              isFullMode
                ? $t("reusable.aiInstructionalBuilderModal.streamingFull", {
                    evaluations: proposed_evaluations.length,
                    activities: items.length,
                  })
                : $t("reusable.aiInstructionalBuilderModal.streaming", {
                    done: items.length,
                    total: expectedActivities,
                  })
            }}
          </small>
        </div>

        <!-- Casilleros que la IA no escribió. La composición la decidió el usuario, así que
             una pieza que falta es algo que él pidió y no llegó: se avisa ACÁ, antes de
             crear, en vez de que aparezca un diseño incompleto después. -->
        <b-alert
          :show="!generating && missing_pieces.length > 0"
          variant="warning"
          class="py-2 px-3 mb-3"
        >
          <div class="font-weight-bold">
            {{
              $t("reusable.aiInstructionalBuilderModal.missingPieces", {
                pieces: countPieces(missing_pieces.length),
              })
            }}
          </div>
          <small class="d-block">
            {{ $t("reusable.aiInstructionalBuilderModal.missingPiecesHint") }}
          </small>
          <small class="d-block mt-1">{{ missingPiecesText }}</small>
        </b-alert>

        <!-- Redistribuir el peso de lo que ya existe. Sin esto, con la asignatura al
             100% las evaluaciones nuevas sólo podían nacer en 0 y había que ir a editarlas
             una por una después — justo la fricción que esta pantalla evita. Se muestran
             sólo las que ponderan. -->
        <div
          v-if="isFullMode && weightedExisting.length > 0"
          class="kl-ai-block mb-3"
        >
          <div class="kl-ai-block-head">
            <b-icon icon="percent" class="kl-ai-block-icon"></b-icon>
            <span class="kl-ai-label">
              {{ $t("reusable.aiInstructionalBuilderModal.redistribute") }}
            </span>
          </div>
          <p class="kl-ai-hint mb-2">
            {{ $t("reusable.aiInstructionalBuilderModal.redistributeHint") }}
          </p>
          <div
            v-for="evaluation in weightedExisting"
            :key="`w-${evaluation.id}`"
            class="d-flex align-items-center mb-1"
          >
            <b-badge :variant="typeVariant(evaluation.type)" pill class="mr-2">
              {{ typeLabel(evaluation.type) }}
            </b-badge>
            <span class="kl-ai-ellipsis flex-grow-1">{{ evaluation.name }}</span>
            <b-input-group size="sm" class="kl-ai-weighing ml-2">
              <b-input-group-prepend is-text>%</b-input-group-prepend>
              <b-form-input
                :value="weighingOf(evaluation)"
                type="number"
                min="0"
                max="100"
                step="1"
                :disabled="busy"
                @input="$set(weighing_edits, evaluation.id, Number($event) || 0)"
              ></b-form-input>
            </b-input-group>
          </div>
        </div>

        <div
          v-for="group in groupedItems"
          :key="`group-${group.evaluation}`"
          class="kl-ai-group"
        >
          <!-- Las actividades sueltas no derivan de ninguna evaluación: se crean
               primero y la evaluación que las mida viene después, cuando el usuario
               la agregue. -->
          <div v-if="group.loose" class="kl-ai-loose-head d-flex align-items-center">
            <b-icon icon="collection" class="mr-2 text-muted"></b-icon>
            <span class="font-weight-bold flex-grow-1">
              {{ $t("reusable.aiInstructionalBuilderModal.looseGroup") }}
            </span>
          </div>
          <b-card
            v-for="item in group.items"
            :key="item.key"
            no-body
            class="kl-ai-card"
            :class="{ 'kl-ai-card-off': item.deleted }"
          >
            <b-card-body class="p-2">
              <div class="d-flex align-items-center">
                <b-form-input
                  v-model="item.name"
                  size="sm"
                  class="kl-ai-inline-input font-weight-bold"
                  :disabled="busy || item.deleted"
                ></b-form-input>
                <b-button
                  variant="link"
                  size="sm"
                  class="kl-ai-icon-btn"
                  :disabled="busy"
                  :title="$t('reusable.aiInstructionalBuilderModal.regenerate')"
                  @click="regenerate(item)"
                >
                  <b-icon icon="arrow-repeat"></b-icon>
                </b-button>
                <b-button
                  variant="link"
                  size="sm"
                  class="kl-ai-icon-btn"
                  :disabled="busy"
                  :title="
                    item.deleted
                      ? $t('reusable.aiInstructionalBuilderModal.restore')
                      : $t('reusable.aiInstructionalBuilderModal.discard')
                  "
                  @click="item.deleted = !item.deleted"
                >
                  <b-icon
                    :icon="item.deleted ? 'arrow-counterclockwise' : 'trash'"
                  ></b-icon>
                </b-button>
              </div>

              <div v-if="item.regenerating" class="d-flex align-items-center py-1">
                <b-spinner small variant="primary" class="mr-2"></b-spinner>
                <small class="text-muted">
                  {{ $t("reusable.aiInstructionalBuilderModal.regenerating") }}
                </small>
              </div>

              <template v-else-if="!item.deleted">
                <b-form-textarea
                  v-model="item.description"
                  size="sm"
                  rows="2"
                  max-rows="6"
                  class="kl-ai-inline-input mt-1"
                  :disabled="busy"
                ></b-form-textarea>

                <!-- El texto que lee el estudiante. Plegado porque es largo y lo primero
                     que se revisa es la idea de la actividad; pero a mano, porque es el
                     único texto que el estudiante va a leer. -->
                <div v-if="item.description_for_student" class="mt-1">
                  <b-button
                    variant="link"
                    size="sm"
                    class="kl-ai-toggle p-0"
                    :disabled="busy"
                    @click="item.show_student = !item.show_student"
                  >
                    <b-icon
                      :icon="item.show_student ? 'chevron-down' : 'chevron-right'"
                    ></b-icon>
                    {{
                      $t("reusable.aiInstructionalBuilderModal.studentInstructions")
                    }}
                  </b-button>
                  <b-form-textarea
                    v-if="item.show_student"
                    v-model="item.description_for_student"
                    size="sm"
                    rows="6"
                    max-rows="24"
                    class="kl-ai-inline-input"
                    :disabled="busy"
                  ></b-form-textarea>
                </div>

                <div class="d-flex align-items-start flex-wrap mt-2">
                  <!-- La actividad cambia según se haga solo, en parejas o en grupo, así
                       que la participación se revisa junto con las horas. -->
                  <b-form-select
                    v-model="item.work_type"
                    :options="workTypeOptions"
                    size="sm"
                    class="kl-ai-work-type mr-2"
                    :disabled="busy"
                  ></b-form-select>
                  <b-input-group size="sm" class="kl-ai-hours mr-2">
                    <b-input-group-prepend is-text>
                      <b-icon icon="clock"></b-icon>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model.number="item.hours"
                      type="number"
                      min="0"
                      step="0.5"
                      :disabled="busy"
                    ></b-form-input>
                    <b-input-group-append is-text>
                      {{ $t("reusable.aiInstructionalBuilderModal.hours") }}
                    </b-input-group-append>
                  </b-input-group>
                  <!-- Lo que la IA eligio de los catalogos. Chips y no
                       selectores: aca se revisa; el ajuste fino se hace
                       despues, en el formulario de la pieza. -->
                  <div class="d-flex flex-wrap kl-ai-tags">
                    <b-badge
                      v-for="tag in tagsOf(item)"
                      :key="`${item.key}-${tag.kind}-${tag.label}`"
                      pill
                      class="kl-ai-tag"
                      :class="`kl-ai-tag-${tag.kind}`"
                      :title="tag.title"
                    >
                      {{ tag.label }}
                    </b-badge>
                    <small
                      v-if="tagsOf(item).length === 0"
                      class="text-muted font-italic"
                    >
                      {{ $t("reusable.aiInstructionalBuilderModal.noTags") }}
                    </small>
                  </div>
                </div>
              </template>
            </b-card-body>
          </b-card>
          <!-- La evaluacion CIERRA su grupo: las actividades la preceden porque
               preparan lo que ella va a medir, que es el orden en que se dicta y el
               mismo en que el diseño las muestra. -->
          <div
            v-if="!group.loose"
            class="kl-ai-group-head d-flex align-items-center"
          >
            <b-badge :variant="typeVariant(group.raw_type)" pill class="mr-2">
              {{ group.type }}
            </b-badge>
            <span class="kl-ai-ellipsis font-weight-bold flex-grow-1">
              {{ group.name }}
            </span>
            <!-- La evaluación propuesta también lleva horas y también se puede
                 ajustar: sin ellas corta la cadena de semanas de todo lo que sigue. -->
            <!-- La ponderación de la evaluación propuesta. Se edita acá porque es el
                 dato que decide si la asignatura cierra en 100%, y revisarlo después en el
                 Programa es tarde. Sólo la llevan Sumativa y Transversal. -->
            <b-input-group
              v-if="group.proposed && (group.raw_type == 3 || group.raw_type == 5)"
              size="sm"
              class="kl-ai-weighing ml-2"
            >
              <b-input-group-prepend is-text>%</b-input-group-prepend>
              <b-form-input
                v-model.number="group.source.weighing"
                type="number"
                min="0"
                max="100"
                step="1"
                :disabled="busy"
              ></b-form-input>
            </b-input-group>
            <b-input-group
              v-if="group.proposed"
              size="sm"
              class="kl-ai-hours ml-2"
            >
              <b-input-group-prepend is-text>
                <b-icon icon="clock"></b-icon>
              </b-input-group-prepend>
              <b-form-input
                v-model.number="group.source.hours"
                type="number"
                min="0"
                step="0.5"
                :disabled="busy"
              ></b-form-input>
              <b-input-group-append is-text>
                {{ $t("reusable.aiInstructionalBuilderModal.hours") }}
              </b-input-group-append>
            </b-input-group>
          </div>
          <!-- Lo que la evaluación declara. Se muestra al lado de sus actividades a
               propósito: es donde se ve de un vistazo si la evidencia que recoge es la
               misma que las actividades producen, que es lo que enciende el cruce de la
               tabla de cobertura. -->
          <div
            v-if="group.proposed && tagsOf(group.source).length > 0"
            class="d-flex flex-wrap kl-ai-tags mb-2"
          >
            <b-badge
              v-for="tag in tagsOf(group.source)"
              :key="`g-${group.evaluation}-${tag.kind}-${tag.label}`"
              pill
              class="kl-ai-tag"
              :class="`kl-ai-tag-${tag.kind}`"
              :title="tag.title"
            >
              {{ tag.label }}
            </b-badge>
          </div>

        </div>

        <!-- E41 · La síntesis del diseño. Va al final del paso 2 y con interruptor propio
             porque no es una pieza más: se escribe sobre un campo Único del diseño, y si ya
             había algo redactado a mano, aceptarla lo reemplaza. Por eso se muestra editable
             y apagada por defecto cuando ya existía un resumen. -->
        <div v-if="!generating && summary_draft" class="kl-ai-summary">
          <div class="d-flex align-items-center mb-2">
            <b-form-checkbox v-model="keep_summary" switch :disabled="busy">
              <span class="font-weight-bold">
                {{ $t("reusable.aiInstructionalBuilderModal.designSummary") }}
              </span>
            </b-form-checkbox>
          </div>
          <b-alert
            :show="keep_summary && design_summary != ''"
            variant="warning"
            class="py-1 px-2 mb-2"
          >
            <small>
              {{ $t("reusable.aiInstructionalBuilderModal.designSummaryReplaces") }}
            </small>
          </b-alert>
          <b-form-textarea
            v-model="summary_draft"
            size="sm"
            rows="5"
            max-rows="16"
            class="kl-ai-inline-input"
            :disabled="busy || !keep_summary"
          ></b-form-textarea>
        </div>

        <div
          v-if="!generating && items.length === 0"
          class="text-center text-muted py-4"
        >
          <b-icon
            icon="inbox"
            font-scale="2"
            class="d-block mx-auto mb-2 kl-ai-empty-icon"
          ></b-icon>
          <small>
            {{ $t("reusable.aiInstructionalBuilderModal.emptyResult") }}
          </small>
        </div>
      </template>

      <!-- Pie. En revision lleva el medidor de horas, porque es el dato que
           decide si lo propuesto entra o hay que recortarlo. -->
      <template #modal-footer>
        <div class="w-100">
          <div v-if="step === 'review'" class="mb-2">
            <div class="d-flex align-items-center justify-content-between">
              <small class="text-muted">
                {{ $t("reusable.aiInstructionalBuilderModal.budget") }}
              </small>
              <small v-if="!time_allocation_id" class="kl-ai-budget-warn">
                <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
                {{ $t("reusable.aiInstructionalBuilderModal.noTimeAllocation") }}
              </small>
              <small v-else class="text-muted">
                <strong class="kl-ai-figures">{{ plannedHours }}</strong>
                {{
                  $t("reusable.aiInstructionalBuilderModal.ofAvailable", {
                    total: available_hours,
                  })
                }}
              </small>
            </div>
            <!-- El tramo sin llenar va en un paso claro del MISMO color, no en
                 gris, para que el estado se lea a lo largo de toda la barra.
                 Mismo criterio que el medidor del "Estado del Diseno". -->
            <b-progress
              height="6px"
              class="kl-ai-meter"
              :class="`kl-ai-meter-${budgetStatus}`"
            >
              <b-progress-bar
                :value="budgetPercentage"
                :variant="budgetStatus === 'empty' ? 'secondary' : budgetStatus"
              ></b-progress-bar>
            </b-progress>
          </div>

          <!-- Ponderación. Sólo cuando se proponen evaluaciones: es lo que decide si la
               asignatura cierra en 100%, y pasarse deja el Programa inconsistente. -->
          <div v-if="step === 'review' && isFullMode" class="mb-2">
            <div class="d-flex align-items-center justify-content-between">
              <small class="text-muted">
                {{ $t("reusable.aiInstructionalBuilderModal.weighing") }}
              </small>
              <small
                :class="weighingStatus === 'danger' ? 'kl-ai-over' : 'text-muted'"
              >
                <b-icon
                  v-if="weighingStatus === 'danger'"
                  icon="exclamation-triangle-fill"
                  class="mr-1"
                ></b-icon>
                <strong class="kl-ai-figures">{{ totalWeighing }}%</strong>
                {{
                  $t("reusable.aiInstructionalBuilderModal.ofTotalWeighing", {
                    used: usedWeighing,
                    planned: plannedWeighing,
                  })
                }}
              </small>
            </div>
            <b-progress
              height="6px"
              class="kl-ai-meter"
              :class="`kl-ai-meter-${weighingStatus}`"
            >
              <b-progress-bar
                :value="weighingPercentage"
                :variant="weighingStatus"
              ></b-progress-bar>
            </b-progress>
          </div>

          <!-- Por qué no se puede crear. Bloquear sin decir el motivo deja al usuario
               tocando un botón muerto. -->
          <div
            v-if="step === 'review' && (weighingOver || hoursOver)"
            class="kl-ai-blocked mb-2"
          >
            <b-icon icon="exclamation-triangle-fill" class="mr-1"></b-icon>
            {{
              weighingOver
                ? $t("reusable.aiInstructionalBuilderModal.blockedWeighing", {
                    total: totalWeighing,
                  })
                : $t("reusable.aiInstructionalBuilderModal.blockedHours", {
                    planned: plannedHours,
                    total: available_hours,
                  })
            }}
          </div>

          <div class="d-flex align-items-center justify-content-end">
            <!-- Rehacer la propuesta entera. Va aparte y en link: es lo que se pide cuando
                 no gustó nada, y cuesta una pasada completa de IA. -->
            <b-button
              v-if="step === 'setup' && hasDraft"
              variant="link"
              size="sm"
              class="mr-auto kl-ai-small px-0"
              :disabled="busy || !canGenerate"
              @click="generate({ all: true })"
            >
              {{ $t("reusable.aiInstructionalBuilderModal.regenerateAll") }}
            </b-button>
            <small v-if="saving" class="text-muted mr-auto">
              <b-spinner small variant="primary" class="mr-1"></b-spinner>
              {{
                $t("reusable.aiInstructionalBuilderModal.savingProgress", {
                  done: saved_count,
                  total: pendingCount,
                })
              }}
            </small>
            <b-button
              variant="secondary"
              size="sm"
              class="mr-2"
              :disabled="busy"
              @click="step === 'review' && !generating ? back() : close()"
            >
              {{
                step === "review" && !generating
                  ? $t("reusable.aiInstructionalBuilderModal.back")
                  : $t("reusable.aiInstructionalBuilderModal.close")
              }}
            </b-button>
            <!-- Volver al paso 2 con lo que ya está escrito, sin pedir nada. -->
            <b-button
              v-if="step === 'setup' && hasDraft"
              variant="outline-primary"
              size="sm"
              class="mr-2"
              :disabled="busy"
              @click="continueToReview"
            >
              {{ $t("reusable.aiInstructionalBuilderModal.continueToReview") }}
            </b-button>
            <b-button
              v-if="step === 'setup'"
              variant="primary"
              size="sm"
              :disabled="!canGenerate || pendingRefs.length === 0"
              @click="generate()"
            >
              <img src="@/assets/icon-ai.svg" alt="" class="kl-ai-icon-sm mr-1" />
              {{
                hasDraft
                  ? $t("reusable.aiInstructionalBuilderModal.generatePending", {
                      count: pendingRefs.length,
                    })
                  : $t("reusable.aiInstructionalBuilderModal.generate")
              }}
            </b-button>
            <b-button
              v-else
              variant="primary"
              size="sm"
              :disabled="busy || pendingCount === 0 || weighingOver || hoursOver"
              @click="save"
            >
              {{
                isFullMode
                  ? $t("reusable.aiInstructionalBuilderModal.saveMixed", {
                      activities: pendingItems.length,
                      evaluations: proposed_evaluations.length,
                    })
                  : $t("reusable.aiInstructionalBuilderModal.save", {
                      count: pendingItems.length,
                    })
              }}
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- E45 · Regenerar una pieza que ya existe. Las mismas opciones que el botón de cada
         tarjeta del diseño, con el componente compartido y no con una copia: dos entradas
         a la misma acción que ofrecieran cosas distintas serían la trampa.
         En su propio modal y no desplegado en la fila: el paso 1 ya muestra los ciclos,
         las piezas que existen y lo que se va a agregar, y meterle tres casillas más al
         medio lo vuelve ilegible. Hay uno solo para todas las piezas —no uno por fila—,
         con `redo_target` diciendo sobre cuál actúa. -->
    <b-modal
      :id="redoModalId"
      :title="
        $t('reusable.aiInstructionalBuilderModal.redoEntity', {
          entity: redo_target ? typeLabel(redo_target.type) : '',
        })
      "
      size="lg"
      ignore-enforce-focus-selector="#sidebar-right"
      :no-close-on-backdrop="busy"
      :no-close-on-esc="busy"
      hide-footer
      @hidden="redo_target = null"
    >
      <template v-if="redo_target">
        <p class="kl-ai-redo-piece">{{ redo_target.name }}</p>
        <RedoPieceFields
          :parts.sync="redo_parts"
          :theme.sync="redo_theme"
          :entity_name="typeLabel(redo_target.type)"
          :disabled="busy"
          @submit="redoPiece(redo_target)"
        ></RedoPieceFields>
        <div class="text-right">
          <b-button
            variant="primary"
            size="sm"
            :disabled="busy || redo_parts.length === 0"
            @click="redoPiece(redo_target)"
          >
            {{ $t("reusable.aiInstructionalBuilderModal.redo") }}
          </b-button>
        </div>
      </template>
    </b-modal>
    <!-- Qué se le envía a la IA. No muestra los valores de esta asignatura —eso es el
         paso 2— sino el inventario de lo que viaja y cómo está estructurado: lo que
         preguntan las instituciones es si la plataforma manda datos de personas y cuánto
         del resultado lo decide el modelo. -->
    <b-modal
      :id="promptModalId"
      :title="$t('reusable.aiInstructionalBuilderModal.promptTitle')"
      size="lg"
      ignore-enforce-focus-selector="#sidebar-right"
      ok-only
      :ok-title="$t('reusable.aiInstructionalBuilderModal.close')"
      scrollable
    >
      <p class="kl-ai-hint mt-0 mb-3">
        {{ $t("reusable.aiInstructionalBuilderModal.promptIntro") }}
      </p>
      <div
        v-for="section in promptSections"
        :key="section.title"
        class="kl-ai-prompt-block"
      >
        <p class="kl-ai-label mb-1">{{ section.title }}</p>
        <ul class="kl-ai-prompt-list mb-0">
          <li v-for="(item, item_index) in section.items" :key="item_index">
            {{ item }}
          </li>
        </ul>
      </div>
    </b-modal>
  </span>
</template>

<script>
import * as names from "@/store/names";
import { toast } from "@/utils/utils";
import useAiBuilderStream from "./ai-builder/useAiBuilderStream";
import {
  createDesignEvaluation,
  patchEvaluation,
  redoPieceWithAi,
} from "@/components/teaching/Instructional/createDesignEvaluation";

// Los tipos que puede tener una evaluación de la línea de tiempo. La Actividad (4) queda
// fuera: es el otro `kind`, no un tipo de evaluación.
const PLAN_TYPES = [1, 2, 3, 5];
// Con qué tipo nace una evaluación agregada con el botón. Formativa es la que menos
// compromete —no cierra nada ni pondera el curso— y el selector de la fila la cambia en
// un clic, así que el botón no tiene que preguntar antes de agregar.
const DEFAULT_EVAL_TYPE = 2;

/**
 * Creación rápida del Diseño Instruccional.
 *
 * Propone ACTIVIDADES a partir de las EVALUACIONES del Programa: el Programa define qué se
 * mide y cuándo, y lo que al diseño le falta es el camino que lleva hasta ahí. Si además se
 * piden evaluaciones, las propone primero y les cuelga sus actividades.
 *
 * Lo que se guarda pasa por `createDesignEvaluation`, el mismo camino que usa el botón de
 * crear a mano.
 */
export default {
  name: "AiInstructionalBuilderModal",
  components: {
    RedoPieceFields: () => import("./ai-builder/RedoPieceFields"),
  },
  props: {
    // E52 · Nula cuando el diseño se abre desde la Asignatura base: ahí no hay plan, y
    // el nivel lo lleva `anchor_id`.
    egress_profile_matter_id: {
      type: Number,
      default: null,
    },
    // Las piezas que el diseño ya tiene, del nivel que corresponda.
    activities: {
      type: Array,
      default: () => [],
    },
    level_key: {
      type: String,
      default: "egress_profile_matter",
    },
    // Id de la Asignatura base o de la del Perfil de Egreso, según el nivel.
    anchor_id: {
      type: Number,
      default: null,
    },
    // Horas pedagógicas que le quedan libres a la asignatura.
    available_hours: {
      type: Number,
      default: 0,
    },
    // Tipo de Hora con el que se crean las filas de horas. Es obligatorio en el modelo, así
    // que sin él las piezas se crean sin horas en vez de fallar.
    time_allocation_id: {
      type: Number,
      default: null,
    },
    // Catálogos, para poder mostrar nombres en vez de ids en la revisión.
    indicators: {
      type: Array,
      default: () => [],
    },
    strategies: {
      type: Array,
      default: () => [],
    },
    evidences: {
      type: Array,
      default: () => [],
    },
    // Las Unidades de Estudio de la Asignatura y los momentos del método del diseño.
    // Son lo que define un ciclo: una Unidad recorrida por todos los momentos.
    study_units: {
      type: Array,
      default: () => [],
    },
    method_moments: {
      type: Array,
      default: () => [],
    },
    // Para crear un Modulo por ciclo: el diseño al que pertenece y cuántos ya tiene,
    // que es de donde sale el `order` del primero nuevo.
    design_summary: {
      type: String,
      default: "",
    },
    instructional_design_id: {
      type: Number,
      default: null,
    },
    // Los Modulos que el diseño ya tiene. Se muestran en el paso 1 para poder
    // agregarles piezas: al reabrir el modal sobre un diseño ya generado, lo útil es
    // seguir trabajando sobre lo que hay, no que te ofrezcan empezar de nuevo.
    modules: {
      type: Array,
      default: () => [],
    },
    show_button: {
      type: Boolean,
      default: false,
    },
    button_class: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      visible: false,
      step: "setup",
      generating: false,
      saving: false,
      error: null,
      items: [],
      // Modo 'full': las evaluaciones que la IA propone. Cada una trae un `ref` temporal al
      // que apuntan sus actividades hasta que existan de verdad.
      proposed_evaluations: [],
      /**
       * LA LÍNEA DE TIEMPO. El único input que decide qué se crea.
       *
       * Cada fila es una pieza: `value` 0 es Actividad y 1/2/3/5 el tipo de evaluación;
       * `unit` es la Unidad de Estudio (null sólo para la integradora). Las filas seguidas
       * de la misma Unidad forman un ciclo, y los momentos se reparten dentro de cada uno.
       *
       * Reemplazó a tres mecanismos que pedían lo mismo de formas distintas —un plan de
       * evaluaciones, actividades por evaluación y actividades sueltas— y que podían
       * contradecirse entre sí. Lo que se compone acá es lo que se crea: no hay pedido que
       * la IA pueda incumplir.
       */
      timeline: [],
      // Los Modulos creados en el intento en curso, para poder deshacerlos si el
      // guardado se cae antes de meter la primera pieza dentro.
      created_modules: [],
      // Cuántos Módulos había al empezar a crear. Sirve para numerar los nuevos sin que la
      // cuenta se mueva bajo los pies mientras se crean. Fuera del guardado, `null`.
      modules_base: null,
      // Regenerar una pieza YA creada: sobre cuál está abierto el modal de opciones, cuál
      // se está regenerando, qué partes se eligieron y con qué tema.
      redo_target: null,
      redoing: null,
      // Si la explicación del paso 1 está abierta. Nace cerrada: lo primero que se tiene
      // que ver es la lista.
      help_open: false,
      redo_theme: "",
      redo_parts: [],
      // E41 · La síntesis del diseño que devolvió la IA, y si el usuario la conserva.
      // Se guarda aparte de las piezas porque no es una pieza: pisa un campo del diseño.
      summary_draft: "",
      keep_summary: true,
      // Los casilleros que la IA dejó sin escribir. Los declara el backend, que sabe
      // cuántos pidió el usuario: sin esto una generación a medias se ve igual que una
      // completa, y el hueco aparecía recién en el diseño ya creado.
      missing_pieces: [],
      saved_count: 0,
      key_seed: 0,
      PLAN_TYPES: PLAN_TYPES,
      DEFAULT_EVAL_TYPE: DEFAULT_EVAL_TYPE,
      // Ponderación editada de las evaluaciones que YA existen, por id. Redistribuir desde
      // acá evita el viaje de ir a cada evaluación del Programa a hacerle lugar a las
      // nuevas, que es justo la fricción que esta pantalla viene a sacar.
      weighing_edits: {},
      // Mientras se agrupan las piezas sueltas en un Modulo nuevo.
      grouping: false,
      // Identificador de cada fila de la línea de tiempo. ES el `ref` del casillero: viaja
      // en el payload y el backend lo usa tal cual. Antes el `ref` era la POSICIÓN, así
      // que agregar o mover una fila corría los de abajo y los borradores del paso 2
      // dejaban de corresponder a su fila.
      row_seed: 1,
    };
  },
  computed: {
    // Ligado a la Asignatura y no fijo: el id tiene que ser único en la página, y nada
    // impide que haya más de un builder montado.
    promptModalId() {
      return `ai-builder-prompt-modal-${this.level_key}-${this.anchor_id}`;
    },
    /**
     * El inventario de lo que viaja a la IA, por secciones.
     *
     * Se arma acá y no en el template porque cada entidad se nombra como la nombra la
     * institución: escrito en el marcado habría que repetir seis `$getVisibleNames` en
     * cada línea.
     */
    promptSections() {
      const t = (key, params) =>
        this.$t(`reusable.aiInstructionalBuilderModal.${key}`, params || {});
      const nombres = {
        entity: this.containerEntityName,
        unit: this.$getVisibleNames(
          "teaching.studyunit",
          false,
          "Unidad de Estudio"
        ),
        units: this.$getVisibleNames(
          "teaching.studyunit",
          true,
          "Unidades de Estudio"
        ),
        indicators: this.$getVisibleNames(
          "teaching.evaluationcriteriamicro",
          true,
          "Indicadores de Logro"
        ),
        strategies: this.$getVisibleNames(
          "mesh.methodologicalstrategy",
          true,
          "Estrategias Metodológicas"
        ),
        evidences: this.$getVisibleNames(
          "manual.evidencia_de_desempeño",
          true,
          "Evidencias de Desempeño"
        ),
      };
      return [
        { key: "Context", items: 4 },
        { key: "Material", items: 5 },
        { key: "Timeline", items: 3 },
        { key: "Fixed", items: 2 },
        { key: "Output", items: 3 },
      ].map((section) => ({
        title: t(`promptSection${section.key}`),
        items: Array.from({ length: section.items }, (_, index) =>
          t(`prompt${section.key}${index + 1}`, nombres)
        ),
      }));
    },
    redoModalId() {
      return `ai-builder-redo-modal-${this.level_key}-${this.anchor_id}`;
    },
    existingEvaluations() {
      return this.activities.filter((x) => x.type != 4);
    },
    // Las piezas que el diseño tiene sin Módulo. No aparecen en la línea de tiempo
    // porque no tienen lugar en la secuencia —eso es lo que aporta el Módulo—, pero sí
    // cuentan para la ponderación de la Asignatura.
    loosePieces() {
      return this.activities
        .filter((x) => x.module == null)
        .sort((a, b) => a.order - b.order || a.id - b.id);
    },
    /**
     * Los Módulos que el diseño YA tiene, con sus piezas y los momentos que ya cubren.
     *
     * Son de sólo lectura acá: lo que se cambia o regenera de una pieza creada se hace en
     * el diseño, con su formulario. Lo que sí se puede es AGREGARLES piezas, y por eso hace
     * falta saber qué momentos les faltan: lo nuevo continúa el recorrido en vez de
     * volver a empezarlo.
     */
    existingModules() {
      // Indicador -> su Unidad. Es lo que permite deducir sobre qué Unidad trabaja un
      // Modulo cuyo `study_units` viene vacío.
      const unit_by_indicator = new Map(
        (this.indicators || []).map((x) => [x.id, x.study_unit])
      );
      return [...this.modules]
        .sort((a, b) => a.order - b.order || a.id - b.id)
        .map((module) => {
          const piezas = this.activities
            .filter((x) => x.module == module.id)
            .sort((a, b) => a.order - b.order || a.id - b.id);
          const cubiertos = new Set();
          piezas.forEach((pieza) =>
            (pieza.moments || []).forEach((id) => cubiertos.add(id))
          );
          // `study_units` del Modulo sólo se llena cuando el Módulo se crea desde acá;
          // uno hecho a mano llega vacío, y la vista del diseño no lo delata porque ahí las
          // Unidades se muestran a partir de los Indicadores de las piezas. Sin Unidad, la
          // fila que se le sumaba se trataba como la integradora: se llevaba el último
          // momento del método —de ahí que todo cayera en el mismo— y, si era actividad, el
          // backend la descartaba entera por no tener de dónde sacar sus Indicadores.
          let unit = (module.study_units || [])[0] || null;
          for (let i = 0; unit == null && i < piezas.length; i++) {
            const ids = piezas[i].evaluation_criterias_micro || [];
            for (const indicator_id of ids) {
              const found = unit_by_indicator.get(indicator_id);
              if (found != null) {
                unit = found;
                break;
              }
            }
          }
          return {
            id: module.id,
            title: module.title,
            unit: unit,
            pieces: piezas,
            uncovered: this.method_moments.filter((x) => !cubiertos.has(x.id)),
          };
        });
    },
    containerEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.container",
        false,
        "Módulo"
      );
    },
    containerEntityNamePlural() {
      return this.$getVisibleNames(
        "manual.instructional_design.container",
        true,
        "Módulos"
      );
    },
    // Hay línea de tiempo: eso es lo que hace que la IA también redacte evaluaciones.
    isCycleMode() {
      return this.timeline.length > 0;
    },
    canAddPiece() {
      return this.timeline.length < 40;
    },
    // Lo que viaja al backend: la línea tal cual, normalizada.
    timelinePayload() {
      return this.timeline.map((row) =>
        row.value
          ? {
              kind: "evaluation",
              type: row.value,
              // El `ref` del casillero. Identifica la FILA y no su posición, que es lo
              // que permite conservar lo ya escrito cuando la línea de tiempo cambia.
              ref: row.uid,
              // De qué va ESTA pieza. El tema general orienta el diseño entero; con varias
              // piezas no alcanza, porque lo que el usuario quiere decidir es cada una.
              theme: row.theme || "",
              unit: row.unit,
              module: row.module || null,
              // El corte viaja: el backend reparte los momentos POR ciclo, así que si
              // agrupara distinto que esta pantalla, el momento que se ve al componer no
              // sería el que la pieza termina teniendo.
              break_run: !!row.break_run,
              // Vacío = el momento lo decide la posición en el ciclo, que es lo normal.
              moments: row.moments || [],
            }
          : {
              kind: "activity",
              ref: row.uid,
              theme: row.theme || "",
              unit: row.unit,
              module: row.module || null,
              break_run: !!row.break_run,
              moments: row.moments || [],
            }
      );
    },
    /**
     * Las Unidades de Estudio que YA están en uso: las del diseño más las de la lista.
     *
     * Las del diseño no se leen del Modulo —su `study_units` sólo se llena cuando el Modulo
     * nace de acá—, sino de los Indicadores de cada pieza, que es de donde la vista del
     * diseño saca la Unidad. Contando sólo la lista decía "0 de ellas" con el diseño ya
     * medio armado, que es justo lo contrario de lo que el número tiene que orientar.
     */
    usedUnitIds() {
      const unit_by_indicator = new Map(
        (this.indicators || []).map((x) => [x.id, x.study_unit])
      );
      const usadas = new Set();
      (this.activities || []).forEach((pieza) =>
        (pieza.evaluation_criterias_micro || []).forEach((indicator_id) => {
          const unit = unit_by_indicator.get(indicator_id);
          if (unit != null) usadas.add(unit);
        })
      );
      (this.modules || []).forEach((module) =>
        (module.study_units || []).forEach((unit) => usadas.add(unit))
      );
      this.timeline.forEach((row) => {
        if (row.unit != null) usadas.add(row.unit);
      });
      return usadas;
    },
    // Las Unidades de Estudio que tiene la asignatura y las que ya están en uso.
    unitsText() {
      return this.$t("reusable.aiInstructionalBuilderModal.unitsAvailable", {
        total: this.countUnits(this.study_units.length),
        used: this.usedUnitIds.size,
      });
    },
    /**
     * Los ciclos: tramos de filas SEGUIDAS de la misma Unidad. Es el mismo criterio del
     * backend (`_build_timeline_slots`), replicado para poder mostrar el momento de cada
     * fila mientras se compone.
     */
    timelineRuns() {
      const runs = [];
      this.timeline.forEach((row, index) => {
        const clave = row.module ? `s${row.module}` : `u${row.unit}`;
        const sigue =
          runs.length > 0 &&
          row.unit != null &&
          runs[runs.length - 1].clave === clave &&
          !row.break_run;
        if (sigue) runs[runs.length - 1].indexes.push(index);
        else
          runs.push({
            clave: clave,
            unit: row.unit,
            module: row.module || null,
            indexes: [index],
          });
      });
      return runs;
    },
    /**
     * Reparte los momentos del método entre las `pieces` filas de un ciclo.
     *
     * Es el mismo reparto que hace el backend (`_split_moments`), replicado acá sólo para
     * mostrarlo mientras se compone: con menos filas que momentos una fila cubre varios
     * momentos seguidos; con más, un momento recibe varias filas. En los dos casos el
     * ciclo recorre todos los momentos en orden, que es la invariante que importa.
     */
    splitMoments() {
      return (pieces, desde) => {
        const momentos = desde || this.method_moments;
        if (!momentos.length || pieces < 1) return [];
        const bloques = [];
        if (pieces <= momentos.length) {
          const base = Math.floor(momentos.length / pieces);
          const extra = momentos.length % pieces;
          let desde = 0;
          for (let index = 0; index < pieces; index++) {
            const largo = base + (index < extra ? 1 : 0);
            bloques.push(momentos.slice(desde, desde + largo));
            desde += largo;
          }
        } else {
          const base = Math.floor(pieces / momentos.length);
          const extra = pieces % momentos.length;
          momentos.forEach((momento, index) => {
            const veces = base + (index < extra ? 1 : 0);
            for (let vuelta = 0; vuelta < veces; vuelta++) bloques.push([momento]);
          });
        }
        return bloques;
      };
    },
    // Los tramos que abren Módulo NUEVO. Los que se suman a uno existente se dibujan
    // dentro de él, no acá.
    newRuns() {
      return this.timelineRuns.filter((run) => !run.module);
    },
    // Índice de fila -> los momentos que le tocan.
    momentsByRow() {
      const mapa = {};
      this.timelineRuns.forEach((run) => {
        let bloques;
        if (run.unit == null) {
          bloques = run.indexes.map(() => this.method_moments.slice(-1));
        } else if (run.module) {
          const modulo = this.existingModules.find((x) => x.id == run.module);
          const faltantes =
            modulo && modulo.uncovered.length > 0
              ? modulo.uncovered
              : this.method_moments.slice(-1);
          bloques = this.splitMoments(run.indexes.length, faltantes);
        } else {
          bloques = this.splitMoments(run.indexes.length);
        }
        run.indexes.forEach((row_index, position) => {
          // Lo que el usuario fijó en la fila gana sobre el reparto, igual que en el
          // backend. Se filtra contra el método para que el orden sea el del recorrido.
          const fijos = this.timeline[row_index].moments || [];
          mapa[row_index] = fijos.length
            ? this.method_moments.filter((x) => fijos.includes(x.id))
            : bloques[position] || [];
        });
      });
      return mapa;
    },
    // Qué casilleros quedaron sin escribir, en el idioma del usuario: el tipo y la Unidad,
    // que es como los reconoce en la línea de tiempo que acaba de componer.
    missingPiecesText() {
      return this.missing_pieces
        .map((piece) => {
          const que =
            piece.kind === "activity"
              ? this.typeLabel(4)
              : piece.type_name || this.$t("reusable.common.name");
          const donde =
            piece.unit_name ||
            this.$t("reusable.aiInstructionalBuilderModal.finalRun");
          return `${que} · ${donde}`;
        })
        .join("; ");
    },
    // Lo que puede ser una pieza: trabajo de clase, o una evaluación de cualquier tipo.
    pieceOptions() {
      return [
        { value: 0, text: this.$t("reusable.aiInstructionalBuilderModal.type4") },
      ].concat(
        PLAN_TYPES.map((value) => ({ value: value, text: this.typeLabel(value) }))
      );
    },

    // Cuántas actividades se van a pedir en total. Es el denominador del progreso del
    // stream, y con evaluaciones pedidas las actividades son sólo de esas.
    expectedActivities() {
      return this.cyclePieceCount;
    },
    // Cuántas piezas trae la línea de tiempo. Es el denominador del progreso del stream.
    cyclePieceCount() {
      return this.timeline.length;
    },
    /**
     * Qué evaluación cierra el ciclo de cada fila, por su `uid`.
     *
     * Es el mismo encadenado que hace el backend (`closes_with`), replicado acá para poder
     * re-apuntar las actividades conservadas cuando la línea de tiempo cambia: si se quita
     * la evaluación de la que colgaban, la actividad queda suelta en vez de apuntar a un
     * casillero que ya no existe.
     */
    closesWithByRow() {
      const mapa = {};
      this.timelineRuns.forEach((run) => {
        let siguiente = null;
        [...run.indexes].reverse().forEach((row_index) => {
          const row = this.timeline[row_index];
          if (row.value) siguiente = row.uid;
          else if (siguiente != null) mapa[row.uid] = siguiente;
        });
      });
      return mapa;
    },
    // Los borradores del paso 2, por `uid` de su fila. Una fila da UNA pieza: actividad o
    // evaluación, nunca las dos.
    draftByRow() {
      const mapa = new Map();
      this.proposed_evaluations.forEach((x) => {
        if (x.ref != null) mapa.set(x.ref, x);
      });
      this.items.forEach((x) => {
        if (x.ref != null) mapa.set(x.ref, x);
      });
      return mapa;
    },
    hasDraft() {
      return this.draftByRow.size > 0;
    },
    // Las filas que hay que pedirle a la IA: las que no tienen borrador, y las que lo
    // tienen desalineado. El resto se conserva tal cual y no cuesta nada.
    pendingRefs() {
      return this.timeline
        .filter((row) => !this.isRowWritten(row.uid))
        .map((row) => row.uid);
    },
    // Las que SÍ tenían borrador pero cambiaron de lugar en el recorrido: su texto fue
    // escrito para otro momento, así que se reescriben.
    staleRefs() {
      return this.timeline
        .filter(
          (row) => this.draftByRow.has(row.uid) && !this.isRowWritten(row.uid)
        )
        .map((row) => row.uid);
    },
    keptRefs() {
      return this.timeline
        .filter((row) => this.isRowWritten(row.uid))
        .map((row) => row.uid);
    },
    // Las que nunca tuvieron borrador: lo que el usuario acaba de agregar a la lista. Se
    // separan de las desalineadas porque no son lo mismo y los números se sumaban mal al
    // leerlos: `pendingRefs` es TODO lo que se le va a pedir a la IA, desalineadas
    // incluidas, así que mostrarlo al lado de ellas parecía contarlas dos veces.
    newRefs() {
      return this.timeline
        .filter((row) => !this.draftByRow.has(row.uid))
        .map((row) => row.uid);
    },
    // Por qué algo YA ESCRITO va a volver a escribirse. Es la parte que no se entendía:
    // el usuario agrega una actividad y de golpe lo que ya estaba escrito deja de servir,
    // sin que nada en pantalla dijera que la culpa es del reparto de momentos del método.
    draftExplain() {
      if (!this.hasDraft || this.staleRefs.length === 0) return "";
      return this.$t("reusable.aiInstructionalBuilderModal.draftStaleWhy");
    },
    // El aviso del paso 1 cuando ya hay borrador: qué se conserva y qué se va a pedir, en
    // una línea por grupo y sin que un número contenga al otro.
    draftLines() {
      if (!this.hasDraft) return [];
      const t = (key, count) =>
        this.$t(`reusable.aiInstructionalBuilderModal.${key}`, { count });
      const lineas = [];
      if (this.keptRefs.length > 0)
        lineas.push(t("draftKept", this.keptRefs.length));
      if (this.staleRefs.length > 0)
        lineas.push(t("draftStale", this.staleRefs.length));
      if (this.newRefs.length > 0)
        lineas.push(t("draftNew", this.newRefs.length));
      // El total sólo cuando hay dos motivos distintos que sumar: con uno solo repetiría
      // el número de la línea anterior.
      if (this.staleRefs.length > 0 && this.newRefs.length > 0)
        lineas.push(t("draftTotal", this.pendingRefs.length));
      if (this.pendingRefs.length === 0)
        lineas.push(
          this.$t("reusable.aiInstructionalBuilderModal.draftComplete")
        );
      return lineas;
    },
    // Con línea de tiempo la IA también redacta evaluaciones, y el paso 2 tiene que
    // mostrarlas con su ponderación y sus horas.
    isFullMode() {
      return this.isCycleMode;
    },
    // De qué evaluaciones cuelgan las actividades: las que ya existen más las propuestas.
    sourceEvaluations() {
      return this.existingEvaluations.concat(this.proposed_evaluations);
    },
    // Alguna actividad regenerándose. Cuenta como "ocupado": el backend atiende una
    // generación por usuario a la vez, así que disparar otra sólo consigue un 429 y deja
    // las dos tarjetas girando.
    regeneratingAny() {
      return this.items.some((x) => x.regenerating) || this.redoing != null;
    },
    busy() {
      return this.generating || this.saving || this.regeneratingAny;
    },
    // Hace falta algo de donde partir: evaluaciones existentes, o evaluaciones pedidas.
    // Hay algo que generar si se compuso al menos una pieza, o si se pidieron actividades
    // para las evaluaciones que ya existen.
    canGenerate() {
      if (this.busy) return false;
      return this.timeline.length > 0 && this.method_moments.length > 0;
    },
    pendingItems() {
      return this.items.filter((x) => !x.deleted && x.name);
    },
    // Lo que el botón de crear va a escribir de verdad: las actividades más las evaluaciones
    // propuestas. Contar sólo las actividades hacía que el botón prometiera menos de lo que
    // hacía y que el progreso se pasara del total.
    pendingCount() {
      return this.pendingItems.length + this.proposed_evaluations.length;
    },
    // El presupuesto lo comparten actividades y, en modo 'full', las evaluaciones que se van
    // a crear: las dos ocupan calendario.
    plannedHours() {
      const piezas = this.isFullMode
        ? this.pendingItems.concat(this.stillProposed)
        : this.pendingItems;
      return (
        Math.round(
          piezas.reduce((sum, x) => sum + Number(x.hours || 0), 0) * 10
        ) / 10
      );
    },
    budgetPercentage() {
      if (!this.available_hours) return 0;
      return Math.min((this.plannedHours / this.available_hours) * 100, 100);
    },
    // El 100% es de la ASIGNATURA. Lo que ya está repartido en sus evaluaciones no se
    // puede volver a usar: proponer sobre eso dejaba el curso en 150% o más.
    // Las que ya existen y llevan peso. Son las únicas que tiene sentido redistribuir.
    weightedExisting() {
      return this.existingEvaluations.filter(
        (x) => x.type == 3 || x.type == 5 || Number(x.weighing || 0) > 0
      );
    },
    weighingOf() {
      // La edición del usuario gana sobre lo guardado.
      return (evaluation) =>
        this.weighing_edits[evaluation.id] != null
          ? Number(this.weighing_edits[evaluation.id])
          : Number(evaluation.weighing || 0);
    },
    usedWeighing() {
      return (
        Math.round(
          this.existingEvaluations.reduce(
            (sum, x) => sum + this.weighingOf(x),
            0
          ) * 10
        ) / 10
      );
    },
    // El total de la ASIGNATURA: lo que ya está más lo que se propone. Es contra 100 que se
    // mide, no contra lo que queda libre — mirar sólo el resto escondía el desbalance.
    totalWeighing() {
      return Math.round((this.usedWeighing + this.plannedWeighing) * 10) / 10;
    },
    weighingOver() {
      // Durante el guardado no se evalúa: `activities` va creciendo con lo recién creado y
      // el total se ve inflado a mitad de camino. Ya se validó antes de empezar, y el
      // usuario no puede hacer nada con la alerta mientras tanto.
      if (this.saving) return false;
      return this.totalWeighing > 100;
    },
    hoursOver() {
      if (this.saving) return false;
      return this.available_hours > 0 && this.plannedHours > this.available_hours;
    },
    availableWeighing() {
      return Math.max(Math.round((100 - this.usedWeighing) * 10) / 10, 0);
    },
    // Las que ya se crearon en esta misma tanda dejan de ser "propuestas": pasan a estar
    // en `activities` porque el PATCH que cierra su vínculo las mete al store. Contarlas de
    // los dos lados hacía saltar la alerta de ponderación en mitad del guardado, como si
    // se estuviera creando una evaluación de más.
    stillProposed() {
      return this.proposed_evaluations.filter((x) => x.proposed !== false);
    },
    plannedWeighing() {
      return (
        Math.round(
          this.stillProposed.reduce((sum, x) => sum + Number(x.weighing || 0), 0) *
            10
        ) / 10
      );
    },
    weighingPercentage() {
      return Math.min(this.totalWeighing, 100);
    },
    weighingStatus() {
      if (this.weighingOver) return "danger";
      if (this.totalWeighing == 100) return "success";
      return "warning";
    },
    budgetStatus() {
      if (!this.available_hours) return "empty";
      if (this.plannedHours > this.available_hours) return "danger";
      if (this.budgetPercentage >= 85) return "warning";
      return "success";
    },
    // Lo último que se lee antes de generar. Dice en una frase qué va a pasar, que es lo que
    // evita la sorpresa de recibir algo distinto de lo esperado.
    summaryText() {
      const partes = [];
      if (this.timeline.length > 0) {
        // Se cuenta lo que el usuario reconoce —actividades y evaluaciones— y no "piezas":
        // fuera del equipo nadie sabe qué es una pieza, y era la primera palabra que leía.
        const actividades = this.timeline.filter((x) => !x.value).length;
        const evaluaciones = this.timeline.length - actividades;
        const que = [];
        if (actividades > 0) que.push(this.countActivities(actividades));
        if (evaluaciones > 0) que.push(this.countEvaluations(evaluaciones));
        // Unidades DISTINTAS, no ciclos: dos Módulos sobre la misma Unidad son una sola
        // Unidad, y la frase habla de Unidades.
        const unidades = new Set(
          this.timeline.map((x) => x.unit).filter((x) => x != null)
        ).size;
        partes.push(
          this.$t("reusable.aiInstructionalBuilderModal.summaryCycle", {
            pieces: que.join(
              ` ${this.$t("reusable.aiInstructionalBuilderModal.and")} `
            ),
            units: this.countUnits(unidades),
            moments: this.method_moments.length,
          })
        );
        // Cómo va a quedar agrupado, dicho en la última línea antes de generar.
        if (this.instructional_design_id)
          partes.push(
            this.$t("reusable.aiInstructionalBuilderModal.summaryModulesOn", {
              entity: this.containerEntityNamePlural,
            })
          );
      }
      return partes.join(" ");
    },
    // Sólo los tres tipos reales. Antes había una cuarta opción con valor nulo que servía
    // de rótulo, pero en la lista se leía como un tipo más de participación.
    workTypeOptions() {
      return [1, 2, 3].map((value) => ({
        value: value,
        text: this.$t(`reusable.aiInstructionalBuilderModal.workType${value}`),
      }));
    },
    // Agrupado por la evaluación de origen, en el orden del Programa.
    groupedItems() {
      const groups = [];
      const by_evaluation = new Map();
      const by_ref = new Map();
      this.sourceEvaluations.forEach((evaluation) => {
        const group = {
          evaluation: evaluation.id,
          name: evaluation.name,
          raw_type: evaluation.type,
          type: this.typeLabel(evaluation.type),
          // `source` es el objeto vivo: el input de horas escribe sobre él, no sobre una
          // copia del grupo, que se recalcula en cada render.
          proposed: evaluation.proposed === true,
          source: evaluation,
          items: [],
        };
        if (evaluation.proposed) by_ref.set(evaluation.ref, group);
        else by_evaluation.set(evaluation.id, group);
        groups.push(group);
      });
      // Las sueltas no derivan de ninguna evaluación, así que no tienen grupo del cual
      // colgar: se juntan en uno propio, que va al final porque es donde se van a crear.
      const loose_group = {
        evaluation: null,
        loose: true,
        proposed: false,
        source: null,
        items: [],
      };
      groups.push(loose_group);
      this.items.forEach((item) => {
        if (item.evaluation == null && item.evaluation_ref == null) {
          loose_group.items.push(item);
          return;
        }
        const group =
          item.evaluation_ref != null
            ? by_ref.get(item.evaluation_ref)
            : by_evaluation.get(item.evaluation);
        if (group) group.items.push(item);
      });
      // Un grupo vacío se oculta SALVO que su evaluación sea una propuesta: ésa se va a
      // crear igual, así que esconderla dejaba al usuario sin poder verla ni editarla —y
      // sin entender por qué aparecía después en el diseño.
      return groups.filter(
        (group) => group.items.length > 0 || group.proposed
      );
    },
  },
  methods: {
    /**
     * La firma de una fila: lo que su borrador tiene que seguir cumpliendo para servir.
     *
     * Son el tipo de pieza, su Unidad, su tema y los momentos del método que le tocan.
     * Cambiarle el tema a una fila ya escrita la reescribe: es justo lo que se pide al
     * escribirlo. Los momentos
     * se reparten POR CICLO, así que insertar una fila en medio de un ciclo le cambia el
     * momento a las vecinas: un texto escrito para "Activación" deja de corresponder y hay
     * que reescribirlo. Lo que NO entra en la firma es de qué evaluación cuelga: eso se
     * re-apunta sin reescribir nada (`syncLinks`).
     */
    rowSignature(uid) {
      const index = this.timeline.findIndex((row) => row.uid == uid);
      if (index < 0) return null;
      const row = this.timeline[index];
      const momentos = (this.momentsByRow[index] || [])
        .map((x) => x.id)
        .join(",");
      return `${row.value}|${row.unit}|${row.theme || ""}|${momentos}`;
    },
    // Si la fila ya tiene su pieza escrita Y sigue siendo la misma fila.
    isRowWritten(uid) {
      const draft = this.draftByRow.get(uid);
      if (!draft || !draft.signature) return false;
      return draft.signature === this.rowSignature(uid);
    },
    // El borrador de una fila se va con ella: quitar una pieza no cuesta una pasada de IA.
    dropDrafts(uid) {
      if (uid == null) return;
      this.items = this.items.filter((x) => x.ref != uid);
      this.proposed_evaluations = this.proposed_evaluations.filter(
        (x) => x.ref != uid
      );
      this.missing_pieces = this.missing_pieces.filter((x) => x.ref != uid);
    },
    /**
     * Re-apunta cada actividad conservada a la evaluación que hoy cierra su ciclo.
     *
     * El vínculo viaja por `ref`, y la línea de tiempo pudo cambiar entre una generación y
     * la siguiente: sin esto, una actividad podía quedar colgando de una evaluación que el
     * usuario quitó, y al guardar nacía suelta sin que nada lo dijera.
     */
    syncLinks() {
      this.items.forEach((item) => {
        // Las de una evaluación que YA existe apuntan por id, y eso no lo mueve nadie.
        if (item.ref == null) return;
        const destino = this.closesWithByRow[item.ref];
        item.evaluation_ref = destino != null ? destino : null;
        if (destino != null) item.evaluation = null;
      });
    },
    // Volver al paso 2 sin pedirle nada a la IA. Es lo que faltaba: el borrador seguía en
    // memoria pero el único camino de vuelta era "Generar", que lo tiraba y lo rehacía.
    continueToReview() {
      this.syncLinks();
      this.error = null;
      this.step = "review";
    },
    // Qué Unidad puede tener una fila. La que cierra el curso es la única que puede no
    // tener ninguna: las recorre todas.
    //
    // El texto de cada opción va recortado porque la lista desplegada de un <select>
    // nativo se estira hasta la opción más larga —no hay CSS que la contenga— y una
    // Unidad sin nombre, rotulada con su RA entero, desbordaba el modal a lo ancho. Lo
    // que distingue una Unidad de otra es el número, que va al principio, y el texto
    // completo se lee en el encabezado del ciclo.
    unitOptions(row) {
      const opciones = this.study_units.map((unit) => ({
        value: unit.id,
        text:
          unit.name.length > 60
            ? `${unit.name.slice(0, 57).trimEnd()}...`
            : unit.name,
      }));
      // CUALQUIER evaluación puede ser el cierre del curso, no sólo la Transversal: hay
      // instituciones que no la usan y cierran con una Sumativa. Lo que hace el cierre no
      // es el tipo, es que la evaluación no pertenezca a ninguna Unidad —las integra
      // todas—, y eso ahora lo decide el usuario fila por fila.
      //
      // Una actividad siempre necesita Unidad: de ahí salen sus Indicadores y sus
      // contenidos, y sin ella el backend la descarta. Y una fila que se suma a un Módulo
      // que ya existe tampoco: pertenece a ese Módulo, no al cierre.
      if (row.value && !row.module) {
        opciones.unshift({
          value: null,
          text: `${this.$t(
            "reusable.aiInstructionalBuilderModal.allUnits"
          )} · ${this.$t("reusable.aiInstructionalBuilderModal.finalRun")}`,
        });
      }
      return opciones;
    },
    // Una fila nueva hereda la Unidad de la anterior: lo normal es seguir componiendo el
    // mismo ciclo, y obligar a elegirla en cada fila sería puro ruido.
    addPiece(value) {
      if (!this.canAddPiece) return;
      // Se hereda de la última fila que TIENE Unidad, no de la última sin más: si la
      // anterior es el cierre del curso —que no pertenece a ninguna— la fila nueva nacía
      // también sin Unidad, así que una actividad entraba al cierre y su selector salía en
      // blanco, porque una actividad no puede ir sin Unidad.
      const con_unidad = [...this.timeline]
        .reverse()
        .find((row) => row.unit != null);
      const unidad =
        (con_unidad ? con_unidad.unit : null) ||
        (this.study_units[0] || {}).id ||
        null;
      // Una Transversal nace FUERA del ciclo anterior y sobre todas las Unidades: es una
      // evaluación que integra el curso entero, y meterla dentro del último Módulo la
      // convertía en el cierre de esa Unidad. Es sólo el valor por defecto: cualquier
      // evaluación puede pasar a cerrar el curso —o dejar de hacerlo— desde su Unidad.
      const transversal = value === 5;
      this.timeline.push({
        uid: this.row_seed++,
        // El tema de la pieza y si su renglón está abierto. Se declaran acá y no
        // después: en Vue 2 una clave que nace fuera del objeto no es reactiva.
        theme: "",
        theme_open: false,
        value: value,
        unit: transversal ? null : unidad,
        // Estos botones agregan al diseño que se está PROPONIENDO, nunca a un Módulo que
        // ya existe: cada Módulo tiene su propio botón para eso, en su encabezado. Antes
        // se heredaba el Módulo de la fila anterior, así que bastaba sumar UNA pieza a un
        // Módulo existente para que todo lo que se agregara después desde abajo cayera
        // dentro de él, sin haberlo pedido.
        module: null,
        break_run: transversal,
        moments: [],
      });
    },
    // Empieza un Módulo nuevo sobre la Unidad que todavía no se usó, con una actividad
    // para que tenga de dónde partir.
    addModule() {
      if (!this.canAddPiece) return;
      const usadas = new Set(
        this.timeline
          .map((x) => x.unit)
          .concat(this.existingModules.map((x) => x.unit))
      );
      const unidad =
        (
          this.study_units.find((x) => !usadas.has(x.id)) ||
          this.study_units[0] ||
          {}
        ).id || null;
      this.timeline.push({
        uid: this.row_seed++,
        // El tema de la pieza y si su renglón está abierto. Se declaran acá y no
        // después: en Vue 2 una clave que nace fuera del objeto no es reactiva.
        theme: "",
        theme_open: false,
        value: 0,
        unit: unidad,
        module: null,
        break_run: true,
        moments: [],
      });
    },
    // El corte es del ciclo, no de la fila: la primera fila de la línea no puede cortar
    // nada, y por eso su botón va deshabilitado.
    toggleBreak(index) {
      const fila = this.timeline[index];
      this.$set(fila, "break_run", !fila.break_run);
    },
    /**
     * Sumar una pieza a un Módulo que se está PROPONIENDO, en su lugar.
     *
     * Va detrás de la última fila de ese ciclo, con su misma Unidad y sin cortar, así que
     * cae dentro de él y continúa su recorrido de momentos. Agregar siempre al final
     * obligaba a subir la fila de a una hasta el Módulo que uno quería.
     */
    addToRun(run, value) {
      if (!this.canAddPiece) return;
      const posicion = Math.max(...run.indexes) + 1;
      this.timeline.splice(posicion, 0, {
        uid: this.row_seed++,
        // El tema de la pieza y si su renglón está abierto. Se declaran acá y no
        // después: en Vue 2 una clave que nace fuera del objeto no es reactiva.
        theme: "",
        theme_open: false,
        value: value,
        unit: run.unit,
        module: run.module || null,
        break_run: false,
        moments: [],
      });
    },
    // Sumar una pieza a un Módulo que ya existe. La fila nace con su Unidad y su
    // Modulo, así que cae dentro de él y continúa su recorrido de momentos.
    addToModule(modulo, value) {
      if (!this.canAddPiece) return;
      // Detrás de las filas que ya se agregaron a ESE Módulo, para que el orden en que se
      // van a crear sea el que se ve.
      let posicion = this.timeline.length;
      for (let index = this.timeline.length - 1; index >= 0; index--) {
        if (this.timeline[index].module == modulo.id) {
          posicion = index + 1;
          break;
        }
      }
      this.timeline.splice(posicion, 0, {
        uid: this.row_seed++,
        // El tema de la pieza y si su renglón está abierto. Se declaran acá y no
        // después: en Vue 2 una clave que nace fuera del objeto no es reactiva.
        theme: "",
        theme_open: false,
        value: value,
        unit: modulo.unit,
        module: modulo.id,
        break_run: false,
        // Vacío es automático. Se declara acá y no se agrega después porque en Vue 2 una
        // clave que nace fuera del objeto no es reactiva.
        moments: [],
      });
    },
    /**
     * Siembra un ciclo entero sobre la Unidad siguiente: una actividad por cada momento
     * del método salvo el último, y una Sumativa que cierra.
     *
     * Es un atajo, no una plantilla: desde ahí se edita fila por fila. Existe porque
     * componer el recorrido completo a mano, para cada Unidad, es el trabajo que esta
     * pantalla viene a ahorrar.
     */
    addCycle() {
      const usadas = new Set(
        this.timeline
          .map((x) => x.unit)
          .concat(this.existingModules.map((x) => x.unit))
      );
      const unidad =
        (this.study_units.find((x) => !usadas.has(x.id)) ||
          this.study_units[0] ||
          {}).id || null;
      const cuantas = Math.max(this.method_moments.length - 1, 1);
      for (let index = 0; index < cuantas; index++) {
        if (!this.canAddPiece) return;
        // La primera fila abre el Módulo: sembrar dos ciclos sobre la misma Unidad daba
        // un solo Módulo del doble de largo.
        this.timeline.push({
          uid: this.row_seed++,
          // El tema de la pieza y si su renglón está abierto. Se declaran acá y no
          // después: en Vue 2 una clave que nace fuera del objeto no es reactiva.
          theme: "",
          theme_open: false,
          value: 0,
          unit: unidad,
          module: null,
          break_run: index === 0,
          moments: [],
        });
      }
      if (this.canAddPiece)
        this.timeline.push({
          uid: this.row_seed++,
          // El tema de la pieza y si su renglón está abierto. Se declaran acá y no
          // después: en Vue 2 una clave que nace fuera del objeto no es reactiva.
          theme: "",
          theme_open: false,
          value: 3,
          unit: unidad,
          module: null,
          break_run: false,
          moments: [],
        });
    },
    removePiece(index) {
      const fila = this.timeline.splice(index, 1)[0];
      if (fila) this.dropDrafts(fila.uid);
    },
    movePiece(index, delta) {
      const destino = index + delta;
      if (destino < 0 || destino >= this.timeline.length) return;
      const fila = this.timeline.splice(index, 1)[0];
      this.timeline.splice(destino, 0, fila);
    },
    // Sólo la integradora puede ir sin Unidad: al dejar de serlo hay que darle una.
    onKindChange(row) {
      // Sólo la ACTIVIDAD exige Unidad. Antes la exigía todo lo que no fuera Transversal,
      // así que cambiarle el tipo al cierre del curso lo metía de vuelta en una Unidad y
      // no había forma de cerrar el curso con una Sumativa.
      if (row.value === 0 && row.unit == null) {
        row.unit = (this.study_units[0] || {}).id || null;
      }
    },
    // Dejar la fila sin Unidad la convierte en el cierre del curso, así que abre su propio
    // Módulo: dentro del ciclo anterior sería el cierre de ESA Unidad, que es justo lo
    // contrario. Volver a elegir una Unidad no toca el corte, que puede haberlo puesto el
    // usuario a mano.
    onUnitChange(row) {
      if (row.unit == null) this.$set(row, "break_run", true);
    },
    // Los contadores van con plural: "1 actividades" es justo el detalle que hace dudar
    // de todo lo demás que dice la pantalla.
    countActivities(count) {
      return this.$tc(
        "reusable.aiInstructionalBuilderModal.countActivities",
        count,
        { count: count }
      );
    },
    countEvaluations(count) {
      return this.$tc(
        "reusable.aiInstructionalBuilderModal.countEvaluations",
        count,
        { count: count }
      );
    },
    countUnits(count) {
      return this.$tc("reusable.aiInstructionalBuilderModal.countUnits", count, {
        count: count,
      });
    },
    // Cuando no se sabe de qué tipo son: "3 actividades y evaluaciones".
    countPieces(count) {
      return this.$tc(
        "reusable.aiInstructionalBuilderModal.countPieces",
        count,
        { count: count }
      );
    },
    // El tema de la fila se escribe en un renglón que se abre bajo ella: puesto en la fila
    // misma, con el tipo, la Unidad y el momento, no cabía nada legible. Cerrarlo con texto
    // escrito NO lo borra —el tema sigue valiendo—, sólo deja de ocupar espacio.
    toggleRowTheme(row) {
      this.$set(row, "theme_open", !row.theme_open);
    },
    // Cada apertura empieza limpia: ni el tema ni las casillas de la pieza anterior
    // tienen por qué volver. Marcar es siempre una decisión explícita.
    openRedo(pieza) {
      this.redo_target = pieza;
      this.redo_theme = "";
      this.redo_parts = [];
      this.$bvModal.show(this.redoModalId);
    },
    /**
     * Regenera una pieza que YA está creada, opcionalmente con otro tema.
     *
     * Las opciones son las mismas que ofrece el botón de cada tarjeta del diseño —el
     * componente es compartido— y los dos llaman a la misma `redoPieceWithAi`, que es
     * donde está escrito qué se toca y qué se conserva.
     *
     * No confirma aparte: las casillas SON la confirmación, y dicen exactamente qué se va
     * a reemplazar, que es más de lo que decía el "¿seguro?" que había antes.
     */
    async redoPiece(pieza) {
      if (this.busy || this.redo_parts.length === 0) return;
      this.redoing = pieza.id;
      // Se cierra antes de empezar: lo que hay que mirar mientras genera es la fila de la
      // pieza, que muestra su propio spinner.
      this.$bvModal.hide(this.redoModalId);
      this.error = null;
      try {
        const fresca = await redoPieceWithAi(this, {
          level_key: this.level_key,
          egress_profile_matter_id: this.egress_profile_matter_id,
          matter_base_id: this.egress_profile_matter_id ? null : this.anchor_id,
          piece_id: pieza.id,
          theme: this.redo_theme,
          parts: this.redo_parts,
        });
        if (!fresca) {
          this.error = this.$t(
            "reusable.aiInstructionalBuilderModal.emptyResult"
          );
          return;
        }
        toast(this.$t("reusable.aiInstructionalBuilderModal.redone"));
        // El diseño de atrás tiene que reflejarlo: la pieza cambió en la base.
        this.$emit("created");
      } catch (e) {
        this.error = e.message;
      } finally {
        this.redoing = null;
        this.redo_theme = "";
        this.redo_parts = [];
      }
    },
    // Las filas de esta tanda que van a un Módulo que ya existe.
    indexesForModule(module_id) {
      const indices = [];
      this.timeline.forEach((row, index) => {
        if (row.module == module_id) indices.push(index);
      });
      return indices;
    },
    // Cómo se llama el ciclo. El número es su posición entre los ciclos con Unidad, así
    // se lee como "Módulo 1", "Módulo 2"; el de la integradora no lleva número porque no
    // es un módulo más, es el cierre del curso.
    runLabel(run) {
      if (run.unit == null) {
        return this.$t("reusable.aiInstructionalBuilderModal.finalRun");
      }
      // Lo que se suma a un Módulo existente lleva SU nombre: es el mismo Módulo.
      if (run.module) {
        const modulo = this.existingModules.find((x) => x.id == run.module);
        if (modulo) return modulo.title;
      }
      // Se numera después de los Módulos que ya existen: si el diseño tiene dos, el
      // primero nuevo es el tercero.
      //
      // La posición sale de `indexOf` sobre el propio ciclo y NO del índice recibido: la
      // plantilla recorre `newRuns` y `createModules` recorre `timelineRuns`, que son dos
      // numeraciones distintas, y el rótulo que se veía no era el que se guardaba.
      //
      // Y la base se congela mientras se crean: cada POST mete su Modulo en el store,
      // así que `existingModules` crecía DENTRO del bucle y los Módulos salían numerados
      // 1, 3, 5, 7 — avanzando de a dos, uno por el recién creado y otro por el ciclo.
      const base =
        this.modules_base != null
          ? this.modules_base
          : this.existingModules.length;
      const numero =
        base + this.newRuns.filter((x) => x.unit != null).indexOf(run) + 1;
      const unidad = this.study_units.find((x) => x.id == run.unit);
      return this.$t("reusable.aiInstructionalBuilderModal.runLabel", {
        entity: this.containerEntityName,
        number: numero,
        unit: unidad ? unidad.name : "",
      });
    },
    // El título con el que NACE el Modulo. A diferencia del rótulo que se ve mientras se
    // compone, no lleva el nombre de la Unidad: eso se guardaba en la base y quedaba viejo
    // apenas alguien editaba el RA —y el nombre del RA ya viaja en la relación, vía
    // InstructionalUnit—. La vista del diseño lo vuelve a componer al mostrarlo.
    runTitle(run) {
      if (run.unit == null) {
        return this.$t("reusable.aiInstructionalBuilderModal.finalRun");
      }
      if (run.module) {
        const modulo = this.existingModules.find((x) => x.id == run.module);
        if (modulo) return modulo.title;
      }
      const base =
        this.modules_base != null
          ? this.modules_base
          : this.existingModules.length;
      return this.$t("reusable.aiInstructionalBuilderModal.runTitle", {
        entity: this.containerEntityName,
        number:
          base + this.newRuns.filter((x) => x.unit != null).indexOf(run) + 1,
      });
    },
    /**
     * Mete en un Módulo nuevo todo lo que estaba suelto.
     *
     * Se hace ACÁ y no al guardar por una razón concreta: el reparto de momentos que el
     * backend calcula necesita saber qué momentos cubre ya el Módulo, y eso lo lee de la
     * base. Existiendo el Módulo antes de generar, lo que se le agregue CONTINÚA el
     * recorrido en vez de volver a empezarlo —que es exactamente el comportamiento que ya
     * tiene cualquier otro Módulo del diseño—.
     *
     * Es el mismo efecto que arrastrarlas a mano en la vista del diseño, y se deshace
     * igual: el Módulo se puede borrar y las piezas vuelven a quedar sueltas, intactas.
     */
    async groupLoosePieces() {
      if (this.grouping || !this.instructional_design_id) return;
      const piezas = this.loosePieces;
      if (piezas.length === 0) return;
      this.grouping = true;
      this.error = null;
      try {
        const module = await this.$store.dispatch(
          names.POST_INSTRUCTIONAL_MODULE,
          {
            title: this.$t("reusable.aiInstructionalBuilderModal.runTitle", {
              entity: this.containerEntityName,
              number: this.existingModules.length + 1,
            }),
            description: "",
            order: this.modules.length + 1,
            instructional_design: this.instructional_design_id,
          }
        );
        // Las Unidades salen de los Indicadores de las piezas, que es de donde se
        // deducen en todo el diseño cuando nadie las declaró.
        const unit_by_indicator = new Map(
          (this.indicators || []).map((x) => [x.id, x.study_unit])
        );
        const unidades = [];
        piezas.forEach((pieza) => {
          (pieza.evaluation_criterias_micro || []).forEach((indicator_id) => {
            const unit = unit_by_indicator.get(indicator_id);
            if (unit != null && !unidades.includes(unit)) unidades.push(unit);
          });
        });
        for (let index = 0; index < unidades.length; index++) {
          await this.$store.dispatch(names.POST_INSTRUCTIONAL_UNIT, {
            module: module.id,
            study_unit: unidades[index],
            content: "",
            order: index + 1,
            status: null,
          });
        }
        for (const pieza of piezas) {
          await patchEvaluation(this, this.level_key, pieza.id, {
            module: module.id,
          });
        }
        toast(
          this.$t("reusable.aiInstructionalBuilderModal.looseGrouped", {
            pieces: this.countPieces(piezas.length),
            entity: this.containerEntityName,
          })
        );
      } catch (e) {
        this.error = this.$t(
          "reusable.aiInstructionalBuilderModal.looseGroupError"
        );
      } finally {
        this.grouping = false;
      }
    },
    /**
     * Crea un Modulo por ciclo y devuelve el mapa ref -> id del Modulo.
     *
     * Un ciclo ES un módulo del diseño, y un módulo en nuestro modelo es un Modulo con
     * sus Unidades de Estudio. Sin esto, todo lo que genera la IA nace suelto y hay que
     * arrastrarlo a mano después, que es justo el trabajo que esta pantalla ahorra. Por eso
     * dejó de ser una casilla que se podía apagar: nadie entendía qué decidía, y la única
     * respuesta correcta era dejarla encendida.
     *
     * El `ref` de una pieza es el `uid` de su fila de la línea de tiempo, que es el mismo
     * con el que el backend numeró los casilleros.
     */
    async createModules() {
      const por_ref = {};
      // Las filas que van a un Modulo existente siempre se ubican en él: la casilla
      // decide sólo si los ciclos NUEVOS crean el suyo.
      this.timeline.forEach((row) => {
        if (row.module) por_ref[row.uid] = row.module;
      });
      if (!this.instructional_design_id) return por_ref;
      this.modules_base = this.existingModules.length;
      this.created_modules = [];
      // Los rótulos se calculan TODOS antes del primer POST, por la misma razón.
      const etiquetas = this.timelineRuns.map((run) => this.runTitle(run));
      let order = this.modules.length;
      for (let run_index = 0; run_index < this.timelineRuns.length; run_index++) {
        const run = this.timelineRuns[run_index];
        // Ya tiene Modulo: las piezas caen dentro del que existe.
        if (run.module) {
          run.indexes.forEach((row_index) => {
            por_ref[this.timeline[row_index].uid] = run.module;
          });
          continue;
        }
        order += 1;
        const module = await this.$store.dispatch(
          names.POST_INSTRUCTIONAL_MODULE,
          {
            title: etiquetas[run_index],
            description: "",
            order: order,
            instructional_design: this.instructional_design_id,
          }
        );
        // El Modulo se liga a su Unidad de Estudio. El de la integradora las lleva
        // TODAS: es lo que lo distingue de un módulo, y es como el modelo lo define.
        const unidades =
          run.unit == null
            ? this.study_units.map((x) => x.id)
            : [run.unit];
        for (let index = 0; index < unidades.length; index++) {
          await this.$store.dispatch(names.POST_INSTRUCTIONAL_UNIT, {
            module: module.id,
            study_unit: unidades[index],
            content: "",
            order: index + 1,
            status: null,
          });
        }
        this.created_modules.push(module.id);
        run.indexes.forEach((row_index) => {
          por_ref[this.timeline[row_index].uid] = module.id;
        });
      }
      this.modules_base = null;
      return por_ref;
    },
    // Qué momento(s) le tocan a la fila. No se elige: es la posición dentro de su ciclo.
    momentsOf(index) {
      return (this.momentsByRow[index] || [])
        .map((moment) => moment.name)
        .join(" + ");
    },
    // Las opciones de momento de una fila. La primera es el automático, y dice cuál sería
    // para no obligar a elegir sólo para saber qué iba a pasar.
    momentOptions(index) {
      const fijos = this.timeline[index].moments || [];
      const automatico = fijos.length
        ? this.$t("reusable.aiInstructionalBuilderModal.momentAutoPlain")
        : this.$t("reusable.aiInstructionalBuilderModal.momentAuto", {
            moment: this.momentsOf(index),
          });
      return [{ value: null, text: automatico }].concat(
        this.method_moments.map((moment) => ({
          value: moment.id,
          text: moment.name,
        }))
      );
    },
    setMoment(index, moment_id) {
      this.timeline[index].moments = moment_id == null ? [] : [moment_id];
    },
    typeLabel(type) {
      return this.$t(`reusable.aiInstructionalBuilderModal.type${type}`);
    },
    // Variantes estándar de Bootstrap para el tipo de evaluación: la misma paleta que viste
    // el resto de los badges de la plataforma.
    typeVariant(type) {
      return (
        { 1: "info", 2: "success", 3: "danger", 4: "primary", 5: "dark" }[
          type
        ] || "secondary"
      );
    },
    // Los catálogos llegan como props para poder mostrar nombres: el backend devuelve ids,
    // que es lo que se guarda, pero un id no se puede revisar. El `kind` los distingue por
    // color, y el texto completo va en el tooltip porque un Indicador entero no entra en un
    // chip.
    tagsOf(item) {
      const tags = [];
      const push = (field, catalog_key, kind) => {
        const catalog = this[catalog_key] || [];
        (item[field] || []).forEach((id) => {
          const found = catalog.find((x) => x.id == id);
          if (!found) return;
          tags.push({
            kind,
            label: found.code || found.name,
            title: found.code ? `${found.code} · ${found.name}` : found.name,
          });
        });
      };
      push("evaluation_criterias_micro", "indicators", "indicator");
      push("methodological_strategies", "strategies", "strategy");
      push("procedure_evidences", "evidences", "evidence");
      return tags;
    },
    // El composable recibe `this` porque `getCurrentInstance()` sólo resuelve dentro de
    // `setup()`, y este componente está escrito con Options API como el resto de la app.
    callBuilder(payload, on_item) {
      const { callBuilder } = useAiBuilderStream(
        {
          url: `/common/instructional-builder-ai/`,
          generate_error_key:
            "reusable.aiInstructionalBuilderModal.generateError",
        },
        this
      );
      return callBuilder(payload, on_item);
    },
    pushEvaluation(raw) {
      if (!raw || !raw.name) return;
      if (this.proposed_evaluations.some((x) => x.ref == raw.ref)) return;
      // Se normaliza al shape de una pieza del diseño (`id`, `type`, `name`) para que el
      // agrupado, los rótulos y el guardado no tengan que saber de dónde salió.
      this.proposed_evaluations.push({
        id: raw.ref,
        ref: raw.ref,
        // La firma de la fila en el momento en que se escribió. Si la fila cambia de lugar
        // en el recorrido, el borrador deja de corresponder y se reescribe.
        signature: this.rowSignature(raw.ref),
        name: raw.name,
        description: raw.description || "",
        type: raw.type,
        weighing: raw.weighing || 0,
        evaluation_criterias_micro: raw.evaluation_criterias_micro || [],
        // La capa del diseño la declaran por igual las evaluaciones y las actividades:
        // desde E37 son la misma fila, y la tabla de cobertura cruza las estrategias y
        // evidencias de un lado contra las del otro.
        description_for_student: raw.description_for_student || "",
        professor_instructions: raw.professor_instructions || "",
        // E41 · Por qué la pieza está en este punto del recorrido.
        orientation: raw.orientation || "",
        work_type: raw.work_type || 1,
        contents: raw.contents || [],
        methodological_strategies: raw.methodological_strategies || [],
        procedure_evidences: raw.procedure_evidences || [],
        moments: raw.moments || [],
        hours: raw.hours || 0,
        module: null,
        learning_experience: null,
        proposed: true,
      });
    },
    pushItem(raw) {
      if (!raw || !raw.name) return;
      // Un casillero es UN lugar del recorrido: si ya hay una pieza con ese `ref`, es la
      // misma escrita otra vez. Igual que en `pushEvaluation`.
      if (raw.ref != null && this.items.some((x) => x.ref == raw.ref)) return;
      this.items.push({
        key: `ai-di-${this.key_seed++}`,
        // El casillero del que salió, en modo ciclo. Es por donde se la regenera.
        ref: raw.ref != null ? raw.ref : null,
        signature: raw.ref != null ? this.rowSignature(raw.ref) : null,
        // Cuelga de una evaluación que ya existe (`evaluation`, id real) o de una que se
        // está proponiendo (`evaluation_ref`). Al guardar, el ref se cambia por el id real.
        // Las dos en null es una actividad suelta: válida, y así queda agrupada aparte.
        evaluation: raw.evaluation != null ? raw.evaluation : null,
        evaluation_ref: raw.evaluation_ref != null ? raw.evaluation_ref : null,
        name: raw.name,
        description: raw.description || "",
        // El texto del estudiante es un campo distinto del `description`, que está escrito
        // para quien diseña. Mezclarlos deja al estudiante leyendo notas de diseño.
        description_for_student: raw.description_for_student || "",
        professor_instructions: raw.professor_instructions || "",
        // E41 · Por qué la pieza está en este punto del recorrido.
        orientation: raw.orientation || "",
        work_type: raw.work_type || 1,
        evaluation_criterias_micro: raw.evaluation_criterias_micro || [],
        contents: raw.contents || [],
        methodological_strategies: raw.methodological_strategies || [],
        procedure_evidences: raw.procedure_evidences || [],
        moments: raw.moments || [],
        hours: raw.hours || 0,
        // El texto del estudiante arranca plegado: es largo y lo primero que se revisa es
        // la idea de la actividad.
        show_student: false,
        deleted: false,
        regenerating: false,
      });
    },
    payload(extra) {
      return Object.assign(
        {
          // El nivel del pedido: el plan si se entró por él, y si no la Asignatura base.
          ...(this.egress_profile_matter_id
            ? { egress_profile_matter: this.egress_profile_matter_id }
            : { matter: this.anchor_id }),
          // La línea de tiempo: lo que se crea. Va tal cual se compuso, con el tema de
          // cada fila dentro: el tema es de la pieza, no del pedido entero.
          timeline: this.timelinePayload,
        },
        // `extra` pisa lo de arriba: al regenerar se manda una línea vacía para que no se
        // vuelvan a redactar las evaluaciones.
        extra || {}
      );
    },
    /**
     * Genera las piezas de la línea de tiempo.
     *
     * INCREMENTAL por defecto: lo que ya está escrito y sigue en su lugar se conserva, y
     * sólo se le piden a la IA los casilleros nuevos —o los que cambiaron de lugar en el
     * recorrido—. Volver al paso 1 a agregar o quitar una pieza costaba antes otra pasada
     * completa sobre piezas que el usuario ya había revisado.
     *
     * Con `{ all: true }` se rehace todo desde cero, que es lo que el usuario pide cuando
     * no le gustó la propuesta entera.
     */
    async generate(options) {
      const todo = !!(options && options.all);
      // Lo que se conserva se decide ANTES de tocar nada: después la línea de tiempo y los
      // borradores se leen entre sí y el orden importa.
      const conservados = todo ? [] : this.items.filter((x) => x.ref == null || this.isRowWritten(x.ref));
      const conservadas = todo
        ? []
        : this.proposed_evaluations.filter((x) => this.isRowWritten(x.ref));
      const escritos = new Set(
        conservadas.map((x) => x.ref).concat(
          conservados.map((x) => x.ref).filter((x) => x != null)
        )
      );
      const pendientes = this.timeline
        .map((row) => row.uid)
        .filter((uid) => !escritos.has(uid));
      const incremental = escritos.size > 0 && pendientes.length > 0;

      this.step = "review";
      this.error = null;
      // COPIAS. `conservados` es la base a la que se vuelve cuando llega el resultado
      // final, y el stream empuja sobre `this.items`: compartir el arreglo hacía que la
      // base creciera con lo que iba llegando y cada pieza nueva terminara dos veces.
      this.items = conservados.slice();
      this.proposed_evaluations = conservadas.slice();
      // Nada que pedir: la línea de tiempo está escrita entera. No se gasta una pasada de
      // IA para llegar al mismo paso 2.
      if (pendientes.length === 0) {
        this.missing_pieces = [];
        this.syncLinks();
        return;
      }
      this.generating = true;
      this.missing_pieces = [];
      // Lo conservado se declara: el presupuesto de ponderación y horas ya está
      // comprometido por esas piezas aunque todavía no existan en la base, y el modelo las
      // necesita como contexto para encadenar lo nuevo con ellas.
      const extra = incremental
        ? {
            only_refs: pendientes,
            written_pieces: conservadas
              .concat(conservados.filter((x) => x.ref != null))
              .map((x) => ({ ref: x.ref, name: x.name })),
            reserved_weighing: conservadas.reduce((acc, x) => {
              acc[x.type] = (acc[x.type] || 0) + Number(x.weighing || 0);
              return acc;
            }, {}),
            reserved_hours:
              Math.round(
                conservadas
                  .concat(conservados)
                  .reduce((sum, x) => sum + Number(x.hours || 0), 0) * 10
              ) / 10,
          }
        : {};
      try {
        // Los ítems llegan por streaming y el evento final trae el resultado validado, que
        // es el autoritativo: se reemplaza lo acumulado.
        const result = await this.callBuilder(this.payload(extra), (event) => {
          // El engine emite el tipo en singular quitando la "s" final de la clave
          // ("activities" -> "activitie"). No es un typo: es su regla.
          if (event.type === "activitie") this.pushItem(event.item);
          else if (event.type === "evaluation") this.pushEvaluation(event.item);
        });
        // Se vuelve a partir de lo conservado: el stream ya había empujado las nuevas y el
        // resultado final las trae validadas.
        this.items = conservados.slice();
        this.proposed_evaluations = conservadas.slice();
        (result.evaluations || []).forEach((x) => this.pushEvaluation(x));
        (result.activities || []).forEach((x) => this.pushItem(x));
        this.missing_pieces = result.missing_pieces || [];
        this.syncLinks();
        // En una pasada incremental el modelo no redacta el diseño entero, así que no
        // escribe síntesis: se conserva la que el modal ya tenía.
        if (!incremental) {
          this.summary_draft = result.design_summary || "";
          // Si el diseño ya tenía una síntesis escrita, la nueva NO entra sola: pisarla sin
          // preguntar borraría un texto que alguien redactó a mano.
          this.keep_summary = !this.design_summary;
        }
      } catch (e) {
        this.error = e.message;
      } finally {
        this.generating = false;
      }
    },
    // Regenerar una sola: se vuelve a pedir y se toma la propuesta que corresponda a su
    // misma evaluación de origen.
    async regenerate(item) {
      item.regenerating = true;
      item.deleted = false;
      this.error = null;
      try {
        // La forma de participación que el usuario haya puesto se le IMPONE a la IA y se
        // conserva acá: regenerar era "dame otra propuesta", y se llevaba puesto el ajuste
        // que uno acababa de hacer.
        const work_type = item.work_type;
        // De una evaluación que ya existe: no hace falta el plan, así que se pide en el
        // modo liviano. De una propuesta: el plan va —los casilleros tienen que seguir
        // alineados para que el `ref` signifique lo mismo— pero el backend devuelve sólo
        // esa actividad en vez de redactar todas las evaluaciones otra vez.
        // En la línea de tiempo el `ref` es el del casillero, que ya trae momento y
        // Unidad: la actividad se regenera en su lugar del recorrido, no en otro.
        const extra = { fixed_work_type: work_type };
        if (item.ref != null) {
          extra.regenerate_ref = item.ref;
        } else {
          extra.timeline = [];
          extra.activities_per_evaluation = 1;
          extra.regenerate_ref = item.evaluation;
        }
        const result = await this.callBuilder(this.payload(extra));
        const fresh = (result.activities || []).find((x) =>
          item.ref != null ? x.ref == item.ref : x.evaluation == item.evaluation
        );
        if (fresh) {
          item.name = fresh.name;
          item.description = fresh.description || "";
          item.description_for_student = fresh.description_for_student || "";
          item.professor_instructions = fresh.professor_instructions || "";
          item.orientation = fresh.orientation || "";
          // Lo elegido por el usuario gana sobre lo que devuelva la IA.
          item.work_type = work_type || fresh.work_type || null;
          item.evaluation_criterias_micro =
            fresh.evaluation_criterias_micro || [];
          item.contents = fresh.contents || [];
          item.methodological_strategies =
            fresh.methodological_strategies || [];
          item.procedure_evidences = fresh.procedure_evidences || [];
          item.moments = fresh.moments || [];
          item.hours = fresh.hours || 0;
        }
      } catch (e) {
        this.error = e.message;
      } finally {
        item.regenerating = false;
      }
    },
    /**
     * La secuencia que queda después de guardar: cada actividad ANTES de la evaluación de la
     * que deriva, en el orden en que se propusieron.
     *
     * Es lo que hace que el diseño se lea como se dicta —se trabaja y después se mide—, y no
     * como un apéndice al final. Se calcula entero antes de escribir nada, porque insertar en
     * el medio corre el `order` de lo que ya existe y eso hay que persistirlo también.
     */
    plannedSequence() {
      const by_evaluation = new Map();
      this.pendingItems.forEach((item) => {
        if (!by_evaluation.has(item.evaluation))
          by_evaluation.set(item.evaluation, []);
        by_evaluation.get(item.evaluation).push(item);
      });
      const sequence = [];
      // Las evaluaciones recién creadas pueden estar YA en `activities`: cerrar su vínculo
      // con el `Evaluation` pasa por una acción del store cuya mutación hace push si no
      // encuentra la fila, así que el prop crece durante el guardado. Sin deduplicar por
      // id, cada una quedaba en la lista dos veces —una por el prop y otra por
      // `proposed_evaluations`— y sus actividades se creaban DOS VECES.
      const por_id = new Map();
      this.activities
        .concat(
          this.isFullMode
            ? this.proposed_evaluations.map((x, index) =>
                Object.assign({}, x, {
                  order: this.activities.length + index + 1,
                })
              )
            : []
        )
        .forEach((piece) => {
          if (!por_id.has(piece.id)) por_id.set(piece.id, piece);
        });
      const base = [...por_id.values()];
      base
        .sort((a, b) => a.order - b.order || a.id - b.id)
        .forEach((piece) => {
          (by_evaluation.get(piece.id) || []).forEach((item) =>
            sequence.push({ item: item, source: piece })
          );
          sequence.push({ existing: piece });
        });
      // Una propuesta cuya evaluación de origen ya no está en la lista (se borró mientras el
      // modal estaba abierto) va al final en vez de perderse.
      by_evaluation.forEach((list, evaluation_id) => {
        if (base.some((x) => x.id == evaluation_id)) return;
        list.forEach((item) => sequence.push({ item: item, source: null }));
      });
      return sequence;
    },
    async save() {
      this.saving = true;
      this.error = null;
      this.saved_count = 0;
      try {
        // Primero la redistribución: las evaluaciones que ya existían se ajustan ANTES de
        // crear las nuevas, para que la asignatura nunca pase por un estado en que la suma
        // se va del 100%.
        for (const evaluation of this.weightedExisting) {
          const nueva = this.weighing_edits[evaluation.id];
          if (nueva == null || Number(nueva) == Number(evaluation.weighing || 0))
            continue;
          await patchEvaluation(this, this.level_key, evaluation.id, {
            weighing: Number(nueva),
          });
        }

        // Los Modulos van PRIMERO: cada pieza nace ya dentro del suyo. Crearlas
        // sueltas y moverlas después las haría saltar de lugar a la vista del usuario.
        const module_by_ref = await this.createModules();

        // Las evaluaciones propuestas se crean ANTES, porque la actividad hereda de ellas el
        // Modulo y el lugar en la secuencia. El `ref` temporal se cambia por el id real.
        if (this.isFullMode) {
          let order = this.activities.length;
          for (const evaluation of this.proposed_evaluations) {
            order += 1;
            const created = await createDesignEvaluation(this, {
              level_key: this.level_key,
              anchor_id: this.anchor_id,
              evaluation: {
                name: evaluation.name,
                description: evaluation.description,
                type: evaluation.type,
                weighing: evaluation.weighing,
                evaluation_criterias_micro:
                  evaluation.evaluation_criterias_micro,
                description_for_student: evaluation.description_for_student,
                professor_instructions: evaluation.professor_instructions,
                orientation: evaluation.orientation,
                work_type: evaluation.work_type,
                contents: evaluation.contents,
                methodological_strategies: evaluation.methodological_strategies,
                procedure_evidences: evaluation.procedure_evidences,
                moments: evaluation.moments,
              },
              // CON horas y sin instrumento. Las horas no son opcionales: una pieza sin
              // ellas corta la cadena del acumulado y deja sin semana a todo lo que viene
              // después. El instrumento sí se carga a mano, en el Programa.
              time_rows:
                evaluation.hours > 0 && this.time_allocation_id
                  ? [
                      {
                        study_environment: null,
                        time_allocation: this.time_allocation_id,
                        hours: evaluation.hours,
                      },
                    ]
                  : [],
              tests: [],
              placement: {
                module: module_by_ref[evaluation.ref] || null,
                order: order,
              },
            });
            // Las actividades apuntan por `ref`; desde acá apuntan al id real.
            this.items.forEach((item) => {
              if (item.evaluation_ref == evaluation.ref) {
                item.evaluation = created.id;
                item.evaluation_ref = null;
              }
            });
            evaluation.id = created.id;
            evaluation.proposed = false;
            this.saved_count += 1;
          }
        }
        const sequence = this.plannedSequence();
        for (let index = 0; index < sequence.length; index++) {
          const entry = sequence[index];
          const order = index + 1;
          // Las piezas que ya existían sólo se corren si su lugar cambió.
          if (entry.existing) {
            if (entry.existing.order != order) {
              await patchEvaluation(this, this.level_key, entry.existing.id, {
                order: order,
              });
            }
            continue;
          }
          const item = entry.item;
          const source = entry.source;
          await createDesignEvaluation(this, {
            level_key: this.level_key,
            anchor_id: this.anchor_id,
            evaluation: {
              name: item.name,
              description: item.description,
              description_for_student: item.description_for_student,
              professor_instructions: item.professor_instructions,
              orientation: item.orientation,
              work_type: item.work_type,
              // Tipo 4: lo que se genera son Actividades, no instancias de calificación.
              // Sin ponderación, por E32.
              type: 4,
              weighing: 0,
              evaluation_criterias_micro: item.evaluation_criterias_micro,
              contents: item.contents,
              methodological_strategies: item.methodological_strategies,
              procedure_evidences: item.procedure_evidences,
              moments: item.moments,
              learning_experience: source ? source.learning_experience : null,
            },
            // Las horas van sin Ambiente, que es como quedan las que se cargan sin elegir
            // uno. El Tipo de Hora sí es obligatorio: sin él no se crea la fila.
            time_rows:
              item.hours > 0 && this.time_allocation_id
                ? [
                    {
                      study_environment: null,
                      time_allocation: this.time_allocation_id,
                      hours: item.hours,
                    },
                  ]
                : [],
            tests: [],
            placement: {
              // El Modulo del ciclo manda; si no hay ciclo, se hereda el de la
              // evaluación de la que deriva.
              module:
                module_by_ref[item.ref] ||
                (source ? source.module : null),
              order: order,
            },
          });
          this.saved_count += 1;
        }
        // Al final y no al principio: si algo de arriba falla, el diseño no queda con un
        // resumen que describe un recorrido que no se alcanzó a crear.
        if (this.keep_summary && this.summary_draft && this.instructional_design_id)
          await this.$store.dispatch(names.PATCH_INSTRUCTIONAL_DESIGN, {
            instructional_design_id: this.instructional_design_id,
            item: { summary: this.summary_draft },
          });
        toast(
          this.$t("reusable.aiInstructionalBuilderModal.saved", {
            pieces: this.countPieces(this.saved_count),
          })
        );
        this.$emit("created");
        // `saving` se apaga ANTES de cerrar: `close()` aborta si algo está en curso, y el
        // `finally` corre después — con lo que el modal quedaba abierto sobre piezas ya
        // creadas, listo para duplicarlas.
        this.saving = false;
        this.close();
      } catch (e) {
        this.error = this.$t("reusable.aiInstructionalBuilderModal.saveError");
        // Si se cayó ANTES de crear la primera pieza, los Modulos recién creados
        // quedaban en el diseño vacíos y sin nada que los explicara, y el reintento
        // agregaba otra tanda igual. Con una pieza ya creada NO se borra nada: ahí lo
        // hecho es parte del diseño y deshacerlo se lo llevaría puesto.
        if (this.saved_count === 0 && this.created_modules.length > 0) {
          for (const module_id of this.created_modules) {
            try {
              await this.$store.dispatch(
                names.DELETE_INSTRUCTIONAL_MODULE,
                module_id
              );
            } catch (error) {
              // Un Modulo que no se pudo borrar no puede tapar el error real.
            }
          }
        }
      } finally {
        this.created_modules = [];
        this.modules_base = null;
        this.saving = false;
      }
    },
    open() {
      this.reset();
      this.visible = true;
    },
    // Se limpia al CERRARSE (evento `hidden` del modal) y no dentro de `close()`: el modal
    // también se cierra por backdrop o Escape, y por ahí no pasaba.
    reset() {
      this.step = "setup";
      this.error = null;
      this.items = [];
      this.proposed_evaluations = [];
      this.missing_pieces = [];
      this.saved_count = 0;
      this.weighing_edits = {};
      this.timeline = [];
      this.redo_target = null;
      this.redoing = null;
      this.redo_theme = "";
      this.redo_parts = [];
      this.summary_draft = "";
      this.keep_summary = true;
      this.modules_base = null;
      this.row_seed = 1;
    },
    back() {
      this.step = "setup";
      this.error = null;
    },
    close() {
      if (this.busy) return;
      this.visible = false;
    },
  },
};
</script>

<style scoped>

/* Con Bootstrap-Vue haciendo el trabajo pesado, acá queda sólo lo que el
   framework no trae: el morado del botón de IA, el indicador de paso, el riel
   que cuelga cada actividad de su evaluación y los colores del medidor. */

/* El mismo morado que el botón de IA del Programa de Asignatura: los botones de
   creación rápida se leen como una familia, no como uno por vista. */
.btn-ai-quick {
  background-color: var(--kl-fourth-hover-color) !important;
  border-color: var(--kl-fourth-hover-color) !important;
  color: var(--kl-text-on-primary) !important;
}
.btn-ai-quick:hover {
  opacity: 0.9;
}
.kl-ai-icon-sm {
  width: 14px;
  height: 14px;
  filter: brightness(0) invert(1);
}
.kl-ai-icon-lg {
  width: 22px;
  height: 22px;
  margin-top: 2px;
}

/* El cuerpo y el pie los dibuja b-modal, así que su marcado no lleva el scope
   de este componente: sin ::v-deep estas reglas no llegarían. */
/* La línea de tiempo tiene cuatro columnas y el paso 2 lista una ficha por pieza: con
   `xl` a secas sigue quedando apretado en una pantalla ancha. Se le deja crecer hasta
   1200px, y en las angostas manda el ancho de la ventana. */
::v-deep .kl-ai-dialog {
  max-width: min(1200px, calc(100vw - 2rem));
}
::v-deep .kl-ai-body {
  max-height: 70vh;
  overflow-y: auto;
  text-align: left;
}
::v-deep .kl-ai-footer {
  background: var(--kl-surface-muted);
}

.kl-ai-title {
  font-weight: var(--kl-fw-bold);
  color: var(--kl-text-heading);
}
.kl-ai-close {
  color: var(--kl-text-muted);
  line-height: 1;
}
.kl-ai-steps {
  display: flex;
  gap: 4px;
}
.kl-ai-step {
  height: 3px;
  flex: 1;
  border-radius: var(--kl-radius-pill);
  background: var(--kl-border-soft);
  transition: background 0.2s ease;
}
.kl-ai-step-done {
  background: var(--kl-color-primary);
}
.kl-ai-label {
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-heading);
}
.kl-ai-small {
  font-size: var(--kl-fs-small);
}
.kl-ai-ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.kl-ai-plan {
  max-width: 22rem;
}
.kl-ai-spin {
  width: 8rem;
}
/* Los tres bloques del paso 1. Cada uno es una decisión distinta, así que se separan
   con superficie propia en vez de quedar como una lista corrida de campos. */
.kl-ai-block {
  border: 1px solid var(--kl-border-soft);
  border-radius: var(--kl-radius-md);
  padding: 10px 12px;
}
/* El bloque de "qué generar" es LA decisión de esta pantalla: se destaca. */
.kl-ai-block-main {
  border-color: var(--kl-color-accent-border);
  background: var(--kl-surface-muted);
}
.kl-ai-block-head {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}
.kl-ai-block-icon {
  color: var(--kl-color-primary);
  margin-right: 6px;
}
.kl-ai-field {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.kl-ai-field-label {
  font-size: var(--kl-fs-small);
  color: var(--kl-text-body);
}
/* El botón que abre la explicación: se lee como una ayuda al margen del título, no como
   una acción del paso. */
.kl-ai-help-btn {
  font-size: var(--kl-fs-caption);
  text-decoration: none;
  white-space: nowrap;
}
/* La explicación abierta, separada del resto con su propio marco: se entiende que es un
   texto de ayuda y no parte de la lista que hay debajo. */
.kl-ai-help {
  border-left: 2px solid var(--kl-border-soft);
  padding: 2px 0 2px 8px;
}
/* El inventario de lo que se envía: líneas cortas y apretadas, para recorrerlas con la
   vista en vez de leerlas como un párrafo. */
.kl-ai-prompt-block + .kl-ai-prompt-block {
  margin-top: var(--kl-space-3);
}
.kl-ai-prompt-list {
  padding-left: 1.1rem;
  font-size: var(--kl-fs-small);
  color: var(--kl-text-body);
}
.kl-ai-hint {
  margin: 4px 0 0;
  font-size: var(--kl-fs-caption);
  color: var(--kl-text-muted);
}
.kl-ai-sep {
  margin: 10px 0;
  border-color: var(--kl-border-soft);
}
/* Los cuatro tipos en grilla: se comparan de un vistazo en vez de leerse en fila. */
.kl-ai-plan {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  gap: 6px;
}
.kl-ai-plan-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border: 1px solid var(--kl-border-soft);
  border-radius: var(--kl-radius-sm);
  background: var(--kl-surface);
  padding: 3px 6px 3px 8px;
}
/* El que tiene cantidad se resalta: dice de un vistazo qué se pidió. */
.kl-ai-plan-row-on {
  border-color: var(--kl-color-accent-border);
  background: var(--kl-color-accent-soft);
}
.kl-ai-plan-badge {
  white-space: nowrap;
}
.kl-ai-source-list {
  max-height: 11rem;
  overflow-y: auto;
  font-size: var(--kl-fs-small);
}
.kl-ai-empty-icon {
  opacity: 0.5;
}

/* ---------- Paso 2 ---------- */
.kl-ai-group + .kl-ai-group {
  margin-top: 16px;
}
.kl-ai-group-head {
  padding-top: 6px;
  margin-top: 6px;
  border-top: 1px solid var(--kl-border-soft);
}
/* Las sueltas sí llevan encabezado arriba: no hay evaluación que cierre el grupo,
   así que sin él no se entiende por qué están juntas. */
/* La vista previa del ciclo: una fila por pieza, en el orden del método. Se lee como
   una escalera para que el recorrido se vea de un vistazo, que es justo lo que no
   se podía ver antes de generar. */
.kl-ai-cycle {
  border-left: 3px solid var(--kl-color-accent-border);
  padding-left: 8px;
}
.kl-ai-cycle-step {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 6px;
  border-radius: 4px;
}
.kl-ai-cycle-step + .kl-ai-cycle-step {
  margin-top: 3px;
}
.kl-ai-cycle-eval {
  background: var(--kl-surface-muted);
  font-weight: 600;
}
/* La línea de tiempo: una fila por pieza. Se lee como una tabla liviana porque lo que
   importa es comparar filas —qué va antes de qué—, no cada fila por separado. */
/* Sin piezas, la lista no existe: hay que decirlo, o no se distingue de algo roto. */
.kl-ai-empty {
  padding: 10px;
  border: 1px dashed var(--kl-border-soft);
  border-radius: 4px;
  color: var(--kl-text-muted);
  font-size: 0.82rem;
  text-align: center;
}
.kl-ai-timeline {
  border: 1px solid var(--kl-border-soft);
  border-radius: 4px;
  padding: 4px 6px;
  max-height: 260px;
  overflow-y: auto;
}
.kl-ai-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
}
/* El encabezado de cada ciclo. Un ciclo es un módulo del diseño, y se dibuja como tal
   para que la línea no se lea como una lista plana de veinte filas. */
.kl-ai-run-head {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--kl-text-heading);
  padding: 6px 2px 3px;
  border-top: 1px solid var(--kl-border-soft);
  margin-top: 4px;
}
.kl-ai-timeline > .kl-ai-run-head:first-child {
  border-top: 0;
  margin-top: 0;
  padding-top: 2px;
}
/* Las filas del ciclo se indentan bajo su encabezado. */
.kl-ai-row-in-run {
  padding-left: 10px;
}
/* Lo que tiene explicación al pasar el mouse. Sin subrayado: ensuciaba la cabecera de
   la tabla, que es justo donde más se mira. */
.kl-ai-col-help {
  cursor: help;
}
/* El renglón del tema de una fila: colgado de ella y sangrado hasta donde empieza su
   contenido, para que se lea como parte de la misma fila y no como otra pieza. */
.kl-ai-row-theme {
  padding: 0 0.4rem 0.4rem 2.2rem;
}
.kl-ai-module + .kl-ai-module {
  margin-top: 4px;
}
/* Lo que ya está creado se muestra apagado y con candado: acá no se edita, se le agrega.
   Lo que se cambia o regenera de una pieza creada se hace en el diseño. */
.kl-ai-row-locked {
  opacity: 0.7;
}
/* El tema de la pieza que se regenera: va pegado a su fila para que se lea como suyo. */
/* El nombre de la pieza sobre la que se va a actuar: el título del modal dice el tipo,
   no cuál de ellas. */
.kl-ai-redo-piece {
  font-weight: var(--kl-fw-semibold);
  margin-bottom: 0.75rem;
}
.kl-ai-row-head {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--kl-text-muted);
  padding-bottom: 3px;
  margin-bottom: 3px;
}
/* La evaluación se destaca: es donde cierra cada tramo del recorrido. */
.kl-ai-row-eval {
  background: var(--kl-surface-muted);
  border-radius: 3px;
}
.kl-ai-col-type {
  flex: 0 0 130px;
  max-width: 130px;
}
/* El tipo también se reconoce por el color del borde, igual que los badges del resto de
   la plataforma: en una lista de veinte filas, leer cada texto es más lento que mirar. */
.kl-ai-type-0 {
  border-left: 3px solid var(--kl-color-accent-border);
}
.kl-ai-type-1 {
  border-left: 3px solid var(--info);
}
.kl-ai-type-2 {
  border-left: 3px solid var(--success);
}
.kl-ai-type-3 {
  border-left: 3px solid var(--danger);
}
.kl-ai-type-5 {
  border-left: 3px solid var(--dark);
}
.kl-ai-col-unit {
  flex: 0 0 150px;
  max-width: 150px;
}
.kl-ai-col-act {
  flex: 0 0 60px;
}
.kl-ai-cycle-type {
  flex: 0 0 120px;
  max-width: 120px;
}
.kl-ai-cycle-num {
  flex: 0 0 20px;
  text-align: center;
  font-size: 0.75rem;
  color: var(--kl-text-muted);
}

/* La lista de actividades a medir. Se acota en alto y hace scroll: un diseño
   avanzado puede tener veinte, y empujar el resumen fuera de la vista dejaba el
   botón de generar sin el contexto que lo explica. */
.kl-ai-measure {
  max-height: 150px;
  overflow-y: auto;
  padding: 6px 8px;
  background: var(--kl-surface-muted);
  border: 1px solid var(--kl-border-soft);
  border-radius: 4px;
}
.kl-ai-measure-row {
  max-width: 100%;
}
.kl-ai-loose-head {
  padding-bottom: 5px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--kl-border-soft);
}
/* Las actividades comparten un riel a la izquierda con la evaluación que cierra
   el grupo: dice "esto prepara lo de abajo" sin repetirlo en cada tarjeta. */
.kl-ai-card {
  border-color: var(--kl-border-soft);
  border-left: 3px solid var(--kl-color-accent-border);
  margin-left: 10px;
}
.kl-ai-card + .kl-ai-card {
  margin-top: 6px;
}
.kl-ai-card-off {
  opacity: 0.55;
  background: var(--kl-surface-muted);
}
/* El nombre y la descripción se editan en el lugar: sin caja hasta que se los
   toca, para que la tarjeta se lea como texto y no como formulario. */
.kl-ai-inline-input {
  border-color: transparent;
  background: transparent;
}
.kl-ai-inline-input:hover:not(:disabled) {
  border-color: var(--kl-border-soft);
}
.kl-ai-inline-input:focus {
  background: var(--kl-surface);
}
.kl-ai-icon-btn {
  color: var(--kl-text-muted);
  line-height: 1;
}
.kl-ai-icon-btn:hover:not(:disabled) {
  color: var(--kl-color-primary);
}
.kl-ai-hours {
  width: 10rem;
}
.kl-ai-weighing {
  width: 7rem;
}
.kl-ai-over {
  color: #b02a37;
}
.kl-ai-blocked {
  background: var(--kl-color-danger-soft);
  color: #b02a37;
  border-radius: var(--kl-radius-md);
  padding: 5px 10px;
  font-size: var(--kl-fs-small);
}
.kl-ai-work-type {
  width: 8.5rem;
}
.kl-ai-toggle {
  font-size: var(--kl-fs-caption);
  color: var(--kl-text-muted);
}
.kl-ai-toggle:hover {
  color: var(--kl-color-primary);
}
.kl-ai-tags {
  flex: 1;
  gap: 4px;
  min-width: 0;
}
/* Tres colores para tres cosas distintas: de qué Indicador de Logro sale, con
   qué se trabaja y qué queda de ese trabajo. */
.kl-ai-tag {
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: var(--kl-fw-medium);
}
.kl-ai-tag-indicator {
  background: var(--kl-color-accent-soft);
  color: #1c5388;
}
.kl-ai-tag-strategy {
  background: #eef3f2;
  color: #40605a;
}
.kl-ai-tag-evidence {
  background: #f3eefb;
  color: #59409b;
}

/* ---------- Medidor de horas ---------- */
.kl-ai-figures {
  color: var(--kl-text-body);
  font-variant-numeric: tabular-nums;
}
/* El ámbar del sistema no alcanza contraste como texto sobre blanco; el rótulo
   usa un paso más oscuro de la misma rampa. */
.kl-ai-budget-warn {
  color: #8a6800;
}
/* El tramo sin llenar va en un paso claro del MISMO color que el relleno, no en
   el gris por defecto de Bootstrap: así el estado se lee a lo largo de toda la
   barra y no sólo en el pedazo lleno. */
.kl-ai-meter-success {
  background-color: var(--kl-color-success-soft);
}
.kl-ai-meter-warning {
  background-color: var(--kl-color-warning-soft);
}
.kl-ai-meter-danger {
  background-color: var(--kl-color-danger-soft);
}
.kl-ai-meter-empty {
  background-color: var(--kl-border-soft);
}
/* La síntesis cierra el paso 2, separada de las tarjetas: habla del diseño entero. */
.kl-ai-summary {
  border-top: 1px solid var(--kl-table-border, #d9e0e0);
  margin-top: 0.9rem;
  padding-top: 0.9rem;
  text-align: left;
}
/* El botón de corte queda encendido mientras la fila abre un Módulo: es un estado de la
   fila, no una acción de una sola vez. */
.kl-ai-icon-on {
  color: var(--kl-color-primary, #006ec8) !important;
}
</style>
